import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEmployeeSession } from '../../contexts/EmployeeSessionProvider';
import {
  CreateEmployeeRequest,
  EmployeeView,
  employeesService,
  UpdateEmployeeRequest,
} from '../../services/employees/employees.service';
import { AreaTableView, AreaView, areasService } from '../../services/areas/areas.service';
import { SessionSalesSummaryView, SessionView, sessionsService, SessionHasOpenOrdersError, type SessionFilterOption } from '../../services/sessions/sessions.service';
import type { SessionCloseOutcome } from '../../services/sessions/sessions.service';
import { notifications } from '@mantine/notifications';
import { toHebrewApiErrorMessage } from '../../services/index/index.service';
import { attendanceService } from '../../services/attendance/attendance.service';
import type { AttendanceActionType, AttendanceActionView } from '../../services/attendance/attendance.service';
import { employeeSessionFingerprint, logEmployeeSessionAudit } from '../../services/employee-session-audit/employee-session-audit.service';
import { PrintJobView, PrinterView, printersService } from '../../services/printers/printers.service';
import { subscribePickerEmployeesEvents } from '../../services/employees-events/employees-events.service';
import { ProductView, productsService, type BulkUpdateProductsRequest } from '../../services/products/products.service';
import { categoriesService, type CategoryReportGroup, type CategoryView } from '../../services/categories/categories.service';
import { VariantComboSelectionModal } from './components/orders/variant-combo-selection-modal/VariantComboSelectionModal';
import { ItemDiscountSelectModal } from './components/receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';
import { transferService } from '../../services/transfer/transfer.service';
import { tablePresenceService } from '../../services/table-presence/table-presence.service';
import { OrderView } from '../../services/orders/orders.service';
import { orderlinesService, OrderlineView } from '../../services/orderlines/orderlines.service';
import { kdsService } from '../../services/kds/kds.service';
import type { PosStreamEvent } from '../../services/pos-stream/pos-stream.service';
import { ValueEntryModal } from '../../components/ValueEntryModal/ValueEntryModal';
import { useQuickCharge, type QuickChargePreparedPayment } from './hooks/useQuickCharge';
import { paymentsService, PaymentlineView } from '../../services/payments/payments.service';
import { SessionControlsSection } from './components/settings/SessionControlsSection';
import { ProductSettingsSection } from './components/settings/product-settings-section/ProductSettingsSection';
import { ImportProductsModal } from './components/settings/import-products/ImportProductsModal';
import { useProductImportExport } from './hooks/useProductImportExport';
import { OptionsBuilderDrawer } from './components/settings/options-builder/OptionsBuilderDrawer';
import { useProductCompositionEditor } from './hooks/product-options/useProductCompositionEditor';
import { PrinterAgentsSection } from './components/settings/PrinterAgentsSection';
import { PrinterRoutingSection } from './components/settings/PrinterRoutingSection';
import { PrinterManagementSection } from './components/settings/PrinterManagementSection';
import { EmployeeIdentificationGate } from './components/auth/EmployeeIdentificationGate';
import { TakeoverConfirmModal, type TakeoverConflict } from './components/auth/TakeoverConfirmModal';
import { PinPadModal } from './components/auth/PinPadModal';
import { PosTopBar, PosTabKey } from './components/topbar/PosTopBar';
import { OfflineBanner } from './components/OfflineBanner/OfflineBanner';
import { useConnectionStatus } from './hooks/useConnectionStatus';
import { GuestNameEditModal } from './components/receipt/guest-name/GuestNameEditModal';
import { ReceiptPrintPreviewModal } from './components/receipt/print-preview/ReceiptPrintPreviewModal';
import { useTransactionReceiptAutoPrint } from './components/receipt/transaction-receipt/useTransactionReceiptAutoPrint';
import { captureReceiptSnapshot } from './components/receipt/shared/receipt.utils';
import { useTakeAwayKitchenAutoPrint } from './hooks/useTakeAwayKitchenAutoPrint';
import { useKitchenFireAutoPrint } from './hooks/useKitchenFireAutoPrint';
import { SettingsHubModal } from './components/settings/SettingsHubModal';
import { RestigoPushesModal } from './components/settings/RestigoPushesModal';
import { PosAssistantPanel } from './components/settings/pos-assistant/PosAssistantPanel';
import { OpenSessionModal } from './components/session/OpenSessionModal';
import { EditVatRateModal } from './components/settings/EditVatRateModal';
import { EditHeaderModal } from './components/settings/EditHeaderModal';
import { AutoCloseSessionModal } from './components/settings/AutoCloseSessionModal';
import { HappyHourActiveModal } from './components/settings/happy-hour/active-modal/HappyHourActiveModal';
import { HappyHourSetupModal } from './components/settings/happy-hour/setup-modal/HappyHourSetupModal';
import { FixedPrinterModal } from './components/settings/FixedPrinterModal';
import { useFixedPrinter } from './hooks/useFixedPrinter';
import { useKioskMode } from './hooks/useKioskMode';
import { useMinOrderReminder } from './hooks/useMinOrderReminder';
import { MinOrderReminderDialog } from './components/orders/min-order/MinOrderReminderDialog';
import type { MinimumOrderPayload } from './components/settings/set-minimum-order/SetMinimumOrderModal';
import { CategoryManagerModal } from './components/settings/category-manager/category-manager-modal/CategoryManagerModal';
import { PastReceiptsModal } from './components/past-receipts/PastReceiptsModal';
import { ZCreditTransactionsModal } from './components/zcredit-transactions/ZCreditTransactionsModal';
import { TransactionReceiptModal } from './components/receipt/transaction-receipt/TransactionReceiptModal';
import { PosAreasSection } from './components/areas/PosAreasSection';
import { KioskOrderTypeScreen } from './components/areas/KioskOrderTypeScreen';
import { PosTableHistorySection } from './components/table-history/PosTableHistorySection';
import { useTableHistoryRestore } from './hooks/useTableHistoryRestore';
import { usePendingApprovals } from './hooks/usePendingApprovals';
import { PosRestoreBillSection } from './components/table-history/PosRestoreBillSection';
import { DailyBriefModal } from './components/areas/daily-brief/DailyBriefModal';
import { PosEmployeesSettingsContent } from './components/settings/PosEmployeesSettingsContent';
import { AttendanceSettingsModal } from './components/settings/AttendanceSettingsModal';
import { AttendanceRulesModal } from './components/settings/AttendanceRulesModal';
import { PosOrdersSection } from './components/orders';
import { ApprovalRequestModal } from './components/orders/approval-request-modal/ApprovalRequestModal';
import { OrderItemActionsFlow } from './components/orders/item-actions/OrderItemActionsFlow';
import { ManualCreditDialog } from './components/orders/split-payment/ManualCreditDialog';
import { TapOnPhoneDialog } from './components/orders/split-payment/TapOnPhoneDialog';
import { QuickChargeMethodModal } from './components/orders/quick-charge/QuickChargeMethodModal';
import type { KitchenPrintGroup } from './components/orders/section/kitchen-print-groups.util';
import { tapOnPhoneBridge } from '../../services/tap-on-phone/tap-on-phone-bridge';
import { PosPrintersSettingsContent } from './components/settings/PosPrintersSettingsContent';
import { PosLimitItemsSection } from './components/limit-items/PosLimitItemsSection';
import { useCanvasHostBounds } from './hooks/useCanvasHostBounds';
import { usePosErrorNotification } from './hooks/usePosErrorNotification';
import { usePosOrdersDomain } from './hooks/usePosOrdersDomain';
import { useActiveSessionPaidOrders } from './hooks/useActiveSessionPaidOrders';
import { usePosSettingsBootstrap } from './hooks/usePosSettingsBootstrap';
import { usePosSettingsUi } from './hooks/usePosSettingsUi';
import {
  requestAndRegisterFirebaseWebPush,
  syncFirebaseWebPushToken,
  triggerLocalWebNotificationTest,
} from '../../services/push/firebase-push.service';
import { useOpenSessionFlow } from './hooks/useOpenSessionFlow';
import { useForeignSessionOpenNotice } from './hooks/useForeignSessionOpenNotice';
import { usePosRefresh } from './hooks/usePosRefresh';
import type { RequestRefresh } from './hooks/useDeferredRefresh';
import { useAppVersionWatch } from '../../hooks/useAppVersionWatch';
import { useReports } from './hooks/useReports';
import { useHappyHourController } from './hooks/useHappyHourController';
import { useHappyHourEffective } from './hooks/useHappyHourEffective';
import { PosReportModal } from './components/reports/PosReportModal';
import { AutoCloseZPrinter } from './components/reports/AutoCloseZPrinter';
import { PosReportRangeModal } from './components/reports/PosReportRangeModal';
import { ZReportLayoutModal } from './components/reports/ZReportLayoutModal';
import { PosSessionPickerModal } from './components/reports/PosSessionPickerModal';
import { AccountantReportModal } from './components/accountant-report/AccountantReportModal';
import { CashDrawerPickModal, type CashDrawerPickPrompt } from './components/cashdrawerdialog/CashDrawerPickModal';
import { useAreaCanvasPresentation } from './hooks/areas/useAreaCanvasPresentation';
import { useAreaTableEditing } from './hooks/areas/useAreaTableEditing';
import { useTableCanvasInteraction } from './hooks/areas/useTableCanvasInteraction';
import { usePosStreamSubscription, CASH_DRAWER_NOT_CONFIGURED_TOAST } from './hooks/stream/usePosStreamSubscription';
import { useDailyBriefController } from './hooks/useDailyBriefController';
import { useVatEditController } from './hooks/useVatEditController';
import { useHeaderEditController } from './hooks/useHeaderEditController';
import { useAuth } from '../../contexts/AuthProvider';
import { pushService } from '../../services/push/push.service';
import { useVariantComboSelection } from './hooks/product-options/useVariantComboSelection';
import { useProductOptions } from './hooks/product-options/useProductOptions';
import { useViewportWidth } from './hooks/useViewportWidth';
import { useStationWaiterSelection, type StationWaiterAction } from './hooks/useStationWaiterSelection';
import { sortEmployeesForPicker } from './components/auth/employee-sort.utils';
import { Modal, Select, SegmentedControl } from '@mantine/core';
import { Button } from '../../components/Button';
import { NumPadModal } from '../../components/NumPad/NumPad';
import { PickListModal } from '../../components/PickListModal/PickListModal';
import { useNavigate } from 'react-router-dom';
import layoutClasses from './pos-shell-page.module.scss';
import navyModal from '../../components/Modal/navy-modal.module.scss';
import sessionCloseClasses from './components/settings/session-close.module.scss';
import { PosSettingsModal } from './components/settings/pos-settings-modal/PosSettingsModal';
import { AreaPrintSettingsModal } from './components/settings/area-print-settings/AreaPrintSettingsModal';
import type { AreaPrintRule } from 'shared-models/browser';
import { formatMoney } from '../../utils/money';
import {
  allocateGlobalTip,
  DEFAULT_ATTENDANCE_REPORT_RULES,
  resolveTip,
  roundMoney,
  type AttendanceReportRules,
} from 'shared-models/browser';
import type {
  AlignmentGapHints,
  AlignmentGuides,
  CanvasInteraction,
  EmployeeEditBaseline,
  EmployeeFormState,
  PosTab,
  ProductEditBaseline,
  ProductFormRow,
  ProductFormState,
  TableFormState,
} from './core/pos-shell-page.types';
import {
  createProductFormRow,
  hasPersistentMergeConflict,
  INITIAL_FORM,
  INITIAL_TABLE_FORM,
  mergeById,
  normalizeArea,
  removeById,
  sortAreasForOperations,
} from './core/pos-shell-page.helpers';
import {
  addProductFormRow,
  applyAreaPrintRuleToProduct,
  buildEmployeeBaseline,
  buildEmployeeEditForm,
  findAreaPrintRuleIdForProduct,
  buildProductBaseline,
  buildProductCreatePayload,
  buildProductEditForm,
  buildProductUpdatePayload,
  buildTableEditForm,
  removeProductFormRow,
  updateProductFormRowField,
  updateProductFormRowImage,
  validateProductFormRows,
} from './core/pos-shell-page.forms';
import {
  buildCancelAllOrderlineSelections,
  buildCancelApprovalPayload,
  type CancelOrderlineSelection,
  hasPaidPaymentline,
  requestCancelApproval,
} from './core/order-cancel-flow';
import type { ApprovalReason } from '../../services/approvals/approvals.service';
import { getSelectableItemPaymentQuantity, computeItemPaymentSplitAmount, isFullyPaidLine } from './components/orders/shared/item-payment.utils';

type PosShellPageProps = {
  tenantId: string;
};

const PERSISTENT_MERGE_CONFLICT_TOAST = {
  color: 'red' as const,
  title: 'לא ניתן למזג',
  message: 'לא ניתן למזג שולחן עם הנחה או טיפ מתמשכים — פנה למנהל',
  autoClose: 3000,
};

export const PosShellPage: React.FC<PosShellPageProps> = ({ tenantId }) => {
  const navigate = useNavigate();
  const { employeeState, setEmployeeState, clearEmployeeState, hydrateEmployeeState } = useEmployeeSession();
  const { isKioskMode, toggleKioskMode } = useKioskMode();
  // Kiosk hides the areas tab, so every landing/reset must resolve to orders instead.
  const landingTab: PosTab = isKioskMode ? 'orders' : 'areas';
  // Ref keeps effects/memoized callbacks off landingTab deps so a kiosk toggle cannot re-run them.
  const landingTabRef = useRef<PosTab>(landingTab);
  landingTabRef.current = landingTab;

  useEffect(() => {
    void syncFirebaseWebPushToken(tenantId).catch(() => {
      // Push registration is best-effort after the user has already granted permission.
    });
  }, [tenantId]);

  const [availableEmployees, setAvailableEmployees] = useState<EmployeeView[]>([]);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [pastReceiptOrderId, setPastReceiptOrderId] = useState<number | null>(null);
  const [takeoverConflict, setTakeoverConflict] = useState<TakeoverConflict | null>(null);
  const [takeoverPinOpen, setTakeoverPinOpen] = useState(false);
  const [isTakingOver, setIsTakingOver] = useState(false);
  const takeoverInFlightRef = useRef(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isAttendanceActioning, setIsAttendanceActioning] = useState(false);
  const [pendingAttendancePrintAction, setPendingAttendancePrintAction] = useState<AttendanceActionView | null>(null);
  const [isAttendancePrintPromptOpen, setIsAttendancePrintPromptOpen] = useState(false);
  const [attendancePrintCandidates, setAttendancePrintCandidates] = useState<PrinterView[] | null>(null);
  const [isResolvingAttendancePrintChoice, setIsResolvingAttendancePrintChoice] = useState(false);
  const attendancePrintRef = useRef<HTMLDivElement>(null);
  const attendancePrintInFlightRef = useRef(false);
  const [manualDrawerPrompt, setManualDrawerPrompt] = useState<CashDrawerPickPrompt | null>(null);
  const [isOpeningManualDrawer, setIsOpeningManualDrawer] = useState(false);
  const [activeTab, setActiveTab] = useState<PosTab>(landingTab);
  const [activeAreas, setActiveAreas] = useState<AreaView[]>([]);
  const [settingsAreas, setSettingsAreas] = useState<AreaView[]>([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [, setIsLoadingAreaSettings] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [, setTableForm] = useState<TableFormState>(INITIAL_TABLE_FORM);
  const [editingTableId, setEditingTableId] = useState<number | null>(null);
  const [isSizeMasterPickMode, setIsSizeMasterPickMode] = useState(false);
  const [, setIsSavingTable] = useState(false);
  const tableMutationInFlightRef = useRef(false);
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
  const [copiedTables, setCopiedTables] = useState<AreaTableView[]>([]);
  const settingsAreasRef = useRef<AreaView[]>([]);
  const [settingsEmployees, setSettingsEmployees] = useState<EmployeeView[]>([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingEmployee, setIsSavingEmployee] = useState(false);
  const [isSavingSession, setIsSavingSession] = useState(false);
  const [isSavingRetroCountedCash, setIsSavingRetroCountedCash] = useState(false);
  const [isLoadingRetroSessionOptions, setIsLoadingRetroSessionOptions] = useState(false);
  const [retroSessionOptions, setRetroSessionOptions] = useState<SessionFilterOption[]>([]);
  const [retroSelectedSessionId, setRetroSelectedSessionId] = useState<number | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [activeSession, setActiveSession] = useState<SessionView | null>(null);
  const [nextOpeningCashSuggestion, setNextOpeningCashSuggestion] = useState(0);
  const [autoCloseTime, setAutoCloseTime] = useState<string | null>(null);
  const [generalItemRequiresManagerApproval, setGeneralItemRequiresManagerApproval] = useState(true);
  const [shiftCloseCashBalancesEnabled, setShiftCloseCashBalancesEnabled] = useState(false);
  const [ordersPageWaiterOwnVisibility, setOrdersPageWaiterOwnVisibility] = useState(false);
  const [manualCreditEntryBlocked, setManualCreditEntryBlocked] = useState(false);
  const [customerNameOnOrderEntry, setCustomerNameOnOrderEntry] = useState(false);
  const [stationWaiterSelectionEnabled, setStationWaiterSelectionEnabled] = useState(false);
  const [cashCloseManagerOnly, setCashCloseManagerOnly] = useState(false);
  const [employeeShiftCloseEnabled, setEmployeeShiftCloseEnabled] = useState(false);
  const [areaPrintRules, setAreaPrintRules] = useState<AreaPrintRule[]>([]);
  const [refundCodeViaPush, setRefundCodeViaPush] = useState(false);
  const [employeeAttendanceReportEnabled, setEmployeeAttendanceReportEnabled] = useState(false);
  const [attendanceUiEnabled, setAttendanceUiEnabled] = useState(true);
  const [attendanceReportRules, setAttendanceReportRules] = useState<AttendanceReportRules>(DEFAULT_ATTENDANCE_REPORT_RULES);
  const [isSavingAttendanceRules, setIsSavingAttendanceRules] = useState(false);
  const [tenantIsTest, setTenantIsTest] = useState(false);
  const [isSavingPosSettings, setIsSavingPosSettings] = useState(false);
  const posSettingsSaveInFlightRef = useRef(false);
  const [floorBackground, setFloorBackground] = useState<'white' | 'image' | 'primary'>('white');
  const [restigoEnabled, setRestigoEnabled] = useState(false);
  const [isDinersNumPadOpen, setIsDinersNumPadOpen] = useState(false);
  const [isCashChangeDialogOpen, setIsCashChangeDialogOpen] = useState(false);
  const pendingMergeAfterDinersRef = useRef(false);
  const [sessionSummary, setSessionSummary] = useState<SessionSalesSummaryView>({ orderCount: 0, salesTotal: 0 });
  const [settingsProducts, setSettingsProducts] = useState<ProductView[]>([]);
  const [settingsCategories, setSettingsCategories] = useState<CategoryView[]>([]);
  const [activePrinters, setActivePrinters] = useState<PrinterView[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingPrinters, setIsLoadingPrinters] = useState(false);
  const [printJobs, setPrintJobs] = useState<PrintJobView[]>([]);
  const [isLoadingPrintJobs, setIsLoadingPrintJobs] = useState(false);
  const [isUpdatingPrintJob, setIsUpdatingPrintJob] = useState(false);
  const [isSendingPrinterTest, setIsSendingPrinterTest] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  // Same-tick re-entry guard (Rule 44) — isSavingProduct alone can't block a double-fire.
  const saveProductInFlightRef = useRef(false);
  const [isTogglingProductStatus, setIsTogglingProductStatus] = useState(false);
  const [updatingProductKdsIds, setUpdatingProductKdsIds] = useState<number[]>([]);
  const updatingProductKdsIdsRef = useRef<Set<number>>(new Set());
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [productFormRows, setProductFormRows] = useState<ProductFormRow[]>(() => [createProductFormRow()]);
  // Summary: Baseline snapshot for product edit diffing so PATCH sends only changed fields.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const [editingProductBaseline, setEditingProductBaseline] = useState<ProductEditBaseline | null>(null);
  const [editingProductAreaPrintRuleId, setEditingProductAreaPrintRuleId] = useState<string | null>(null);
  // Manual-close Z print: holds the session-closed refresh until the print POST settles.
  const [isManualZPrintInFlight, setIsManualZPrintInFlight] = useState(false);
  const [form, setForm] = useState<EmployeeFormState>(INITIAL_FORM);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [editingEmployeeId, setEditingEmployeeId] = useState<number | null>(null);
  // Summary: Baseline snapshot for employee edit diffing so PATCH sends only changed fields.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const [editingEmployeeBaseline, setEditingEmployeeBaseline] = useState<EmployeeEditBaseline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGuestNameEditOpen, setIsGuestNameEditOpen] = useState(false);
  const [isReceiptPreviewOpen, setIsReceiptPreviewOpen] = useState(false);
  // Per-row חשבון bill preview: the fetched order plus its on-demand lines/payments.
  const [billPreview, setBillPreview] = useState<{
    order: OrderView;
    orderlines: OrderlineView[];
    paymentlines: PaymentlineView[];
  } | null>(null);
  const billPreviewInFlightRef = useRef(false);
  const [transferableOrderlines, setTransferableOrderlines] = useState<import('../../services/orderlines/orderlines.service').OrderlineView[]>([]);
  const [transferRemainingToPay, setTransferRemainingToPay] = useState<number | null>(null);
  const [isLoadingTransferableOrderlines, setIsLoadingTransferableOrderlines] = useState(false);
  const [cancelTableConfirm, setCancelTableConfirm] = useState<{ tableId: number; tableLabel: string; orderId: number } | null>(null);
  const [cancelTableApprovalRequest, setCancelTableApprovalRequest] = useState<{
    orderId: number;
    tableLabel: string;
    items: CancelOrderlineSelection[];
    orderlines: OrderlineView[];
    cancelScope: 'order';
  } | null>(null);
  const [isSubmittingCancelTableApproval, setIsSubmittingCancelTableApproval] = useState(false);
  const [isCancelingTable, setIsCancelingTable] = useState(false);
  const cancelTableInFlightRef = useRef(false);
  const cancelTableApprovalInFlightRef = useRef(false);
  const interactionRef = useRef<CanvasInteraction | null>(null);
  const isPersistingInteractionRef = useRef(false);
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuides>({ vertical: null, horizontal: null });
  const [alignmentGapHints, setAlignmentGapHints] = useState<AlignmentGapHints>({ horizontal: null, vertical: null });
  const editingTableIdRef = useRef<number | null>(null);
  const openDrawerInFlightRef = useRef(false);
  const closeSessionInFlightRef = useRef(false);
  const retroCountedCashInFlightRef = useRef(false);
  const broadcastRefreshInFlightRef = useRef(false);
  const createCategoryInFlightRef = useRef(false);
  const requestRefreshRef = useRef<RequestRefresh>(() => undefined);
  const broadcastRefreshRef = useRef<() => void>(() => undefined);
  const requestEditOpeningCashRef = useRef<() => void>(() => undefined);
  const {
    hostRef: viewCanvasHostRef,
    width: viewCanvasHostWidth,
    height: viewCanvasHostHeight,
  } = useCanvasHostBounds();
  const {
    hostRef: editCanvasHostRef,
    width: editCanvasHostWidth,
    height: editCanvasHostHeight,
  } = useCanvasHostBounds();
  const viewportWidth = useViewportWidth();

  // בחירת מלצר בעמדה נייחת: per-entry waiter picker; disabled on phone widths.
  const stationWaiterProceedRef = useRef<(action: StationWaiterAction) => void>(() => undefined);
  const stationWaiter = useStationWaiterSelection({
    enabled: stationWaiterSelectionEnabled && viewportWidth >= 768,
    onProceed: (action) => stationWaiterProceedRef.current(action),
  });
  const { isOffline, markStreamDown, markStreamAlive } = useConnectionStatus();
  usePosErrorNotification(error, setError);

  settingsAreasRef.current = settingsAreas;
  editingTableIdRef.current = editingTableId;

  const currentEmployee = employeeState?.employee?.tenantId === tenantId ? employeeState.employee : null;
  const employeeSession =
    employeeState?.employee?.tenantId === tenantId ? employeeState.employeeSession : null;
  const activeSessionId =
    employeeState?.employee?.tenantId === tenantId ? employeeState.activeSessionId : null;
  const isManager = currentEmployee?.role === 'manager';
  const { user, availableTenants } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const tenantPhotoUrl = availableTenants.find((option) => option.tenantId === tenantId)?.photoUrl ?? null;
  const vatEdit = useVatEditController({
    tenantId,
    onError: setError,
    // A VAT rate change must reach every device's accountant report; broadcast a refresh.
    onVatRateChanged: () => broadcastRefreshRef.current(),
  });
  const headerEdit = useHeaderEditController({
    tenantId,
    onError: setError,
  });

  // Table-history + שחזר שולחן flow (state + handlers) lives in its own controller hook (Rule 32).
  const {
    tableHistoryTarget,
    restoreSourceOrder,
    restoreRequest,
    handleTableHistoryLongPress,
    handleSelectHistoryOrder,
    closeRestoreBill,
    openRestoreBillForOrderId,
    handleRestoreRequest,
    handleCancelRestorePick,
    handlePickRestoreTable,
  } = useTableHistoryRestore({ tenantId, employeeSession, isManager, setActiveTab });

  // Post-render-fresh selected order id; gates the approval cancel-order eject to the viewing device.
  const selectedOrderIdForApprovalRef = useRef<number | null>(null);
  const {
    pendingApprovalIds,
    setPendingApprovalIds,
    setPendingApprovalOrderIds,
    approvalEventCallbackRef,
    cancelAllSentLinesCallbackRef,
    handleApprovalEvent,
    showPendingApprovalBlockingToast,
    ensureOrderHasPendingApproval,
  } = usePendingApprovals({
    tenantId,
    employeeSession,
    isManager,
    currentEmployeeId: currentEmployee?.id ?? null,
    activeTab,
    setActiveTab,
    selectedOrderIdRef: selectedOrderIdForApprovalRef,
  });

  const composition = useProductCompositionEditor(tenantId, employeeSession);

  const showManualDrawerError = useCallback((drawerError: unknown) => {
    const drawerMsg = drawerError instanceof Error ? drawerError.message : '';
    if (/no active cash-drawer/i.test(drawerMsg)) {
      // Same friendly hint as the auto paths instead of a raw error.
      notifications.show(CASH_DRAWER_NOT_CONFIGURED_TOAST);
      return;
    }
    notifications.show({
      color: 'red',
      title: 'שגיאה',
      message: toHebrewApiErrorMessage(drawerMsg),
      autoClose: 3000,
    });
  }, []);

  const openCashDrawerById = useCallback(async (printerId: number) => {
    if (!employeeSession || !isManager || openDrawerInFlightRef.current) {
      return;
    }
    openDrawerInFlightRef.current = true;
    setIsOpeningManualDrawer(true);
    try {
      await printersService.openCashDrawer(tenantId, employeeSession, printerId);
      setManualDrawerPrompt(null);
      notifications.show({ color: 'green', message: 'מגירת הקופה נפתחה', autoClose: 3000 });
    } catch (drawerError) {
      showManualDrawerError(drawerError);
    } finally {
      openDrawerInFlightRef.current = false;
      setIsOpeningManualDrawer(false);
    }
  }, [employeeSession, isManager, showManualDrawerError, tenantId]);

  // Summary: "פתח מגירה" — manager fires the cash-drawer kick on demand; when multiple
  // drawers exist we prompt for a specific one before enqueuing the kick.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const handleOpenCashDrawer = useCallback(async () => {
    if (!employeeSession || !isManager || openDrawerInFlightRef.current) {
      return;
    }

    openDrawerInFlightRef.current = true;
    setIsOpeningManualDrawer(true);
    try {
      const activePrinters = await printersService.listActive(tenantId, employeeSession);
      const drawers = activePrinters
        .filter((printer) => printer.cashDrawer)
        .map((printer) => ({ id: printer.id, name: printer.name }));

      if (drawers.length === 0) {
        notifications.show(CASH_DRAWER_NOT_CONFIGURED_TOAST);
        return;
      }

      if (drawers.length > 1) {
        setManualDrawerPrompt({ drawers });
        return;
      }

      await printersService.openCashDrawer(tenantId, employeeSession, drawers[0].id);
      notifications.show({ color: 'green', message: 'מגירת הקופה נפתחה', autoClose: 3000 });
    } catch (drawerError) {
      showManualDrawerError(drawerError);
    } finally {
      openDrawerInFlightRef.current = false;
      setIsOpeningManualDrawer(false);
    }
  }, [employeeSession, isManager, showManualDrawerError, tenantId]);

  const reports = useReports({ tenantId, employeeSession });
  const [accountantReportOpen, setAccountantReportOpen] = useState(false);

  // Thin wrappers; usePosRefresh (below, after settings) fills both refs with the real impls.
  const handleSelfRefresh = useCallback(() => {
    requestRefreshRef.current('self');
  }, []);
  const handleBroadcastRefresh = useCallback(() => {
    broadcastRefreshRef.current();
  }, []);
  const handleOpenEditOpeningCash = useCallback(() => {
    requestEditOpeningCashRef.current();
  }, []);

  const handleTestPushRegistration = useCallback(() => {
    void (async () => {
      try {
        const registration = await requestAndRegisterFirebaseWebPush(tenantId);
        if (registration.status !== 'registered') {
          notifications.show({
            color: 'orange',
            title: 'התראות פוש לא הופעלו',
            message: registration.message,
            autoClose: 4000,
          });
          return;
        }

        const localTest = await triggerLocalWebNotificationTest();
        if (!localTest.ok) {
          notifications.show({
            color: 'orange',
            title: 'בדיקה מקומית נכשלה',
            message: localTest.message,
            autoClose: 4000,
          });
        }

        const result = await pushService.testRegistration({
          tenantId,
          employeeSession,
          pushToken: registration.pushToken,
        });

        if (result.forwarded) {
          notifications.show({
            color: 'green',
            title: 'בדיקת פוש הצליחה',
            message: 'הטוקן נרשם ונשלח. אם לא ראית התראה, בדוק הרשאות מערכת לדפדפן.',
            autoClose: 3500,
          });
          return;
        }

        notifications.show({
          color: 'orange',
          title: 'בדיקת פוש חלקית',
          message: result.warning ?? 'הטוקן קיים מקומית אך לא הועבר ליעד.',
          autoClose: 3500,
        });
      } catch (error: unknown) {
        notifications.show({
          color: 'red',
          title: 'בדיקת פוש נכשלה',
          message: toHebrewApiErrorMessage(error instanceof Error ? error.message : String(error)),
          autoClose: 3000,
        });
      }
    })();
  }, [tenantId, employeeSession]);

  const {
    happyHour,
    setHappyHour,
    isHappyHourSetupOpen,
    isHappyHourActiveOpen,
    openHappyHour,
    closeHappyHourSetup,
    closeHappyHourActive,
  } = useHappyHourController({ tenantId, employeeSession });

  const { effective: happyHourEffective } = useHappyHourEffective(happyHour);

  const {
    settingsHubOpen,
    setSettingsHubOpen,
    settingsModalKey,
    setSettingsModalKey,
    areaMode,
    setAreaMode,
    isSetMinimumMode,
    setIsSetMinimumMode,
    isSettingsActive,
    isSettingsSubFlowActive,
    handleSelectSettingsTile,
  } = usePosSettingsUi({
    setActiveTab,
    onNavigateKds: () => navigate(`/t/${tenantId}/kds`, { state: { from: `/t/${tenantId}/pos` } }),
    onNavigateBackoffice: () => navigate(`/t/${tenantId}/backoffice`),
    onOpenCashDrawer: () => {
      void handleOpenCashDrawer();
    },
    onOpenReport: (key) => reports.handleOpenReport(key),
    onOpenTestPush: handleTestPushRegistration,
    onOpenVatRate: () => {
      void vatEdit.open();
    },
    onOpenHeaderEdit: () => {
      void headerEdit.open();
    },
    onOpenAccountantReport: () => setAccountantReportOpen(true),
    onOpenEditOpeningCash: handleOpenEditOpeningCash,
    onSelfRefresh: handleSelfRefresh,
    onBroadcastRefresh: handleBroadcastRefresh,
    onOpenHappyHour: openHappyHour,
    onOpenTakeAway: () => { void handleOpenTakeAwayClick(); },
    onOpenDailyBrief: () => dailyBrief.open(),
    onOpenQuickCharge: () => {
      if (stationWaiter.intercept({ kind: 'quick_charge' })) return;
      void quickCharge.start();
    },
    onToggleKioskMode: toggleKioskMode,
  });

  useEffect(() => {
    if (isKioskMode && activeTab === 'areas' && areaMode === 'view') {
      setActiveTab('orders');
    }
  }, [activeTab, areaMode, isKioskMode]);

  const { requestReceiptAutoPrint, queueHeadlessReceiptPrint, autoPrintElements, closeFallback: closePostPaymentScreen, isPostPaymentScreenOpen } = useTransactionReceiptAutoPrint(
    tenantId,
    employeeSession,
    () => setActiveTab(landingTabRef.current),
  );
  const { requestTakeAwayKitchenPrint, kitchenPrintElements } = useTakeAwayKitchenAutoPrint(
    tenantId,
    employeeSession,
    stationWaiter.actingWaiter?.name ?? currentEmployee?.fullName ?? null,
    stationWaiter.actingWaiter?.id ?? null,
  );
  const { requestKitchenFireDispatch, kitchenFirePrintElements } = useKitchenFireAutoPrint(
    tenantId,
    employeeSession,
    stationWaiter.actingWaiter?.id ?? null,
  );
  // Partial receipt prints directly; falls back to the picker modal if no printer is locked.
  const handleShowPartialReceipt = useCallback((orderId: number, paymentlineId: number) => {
    void requestReceiptAutoPrint(orderId, { paymentlineId });
  }, [requestReceiptAutoPrint]);

  const {
    activeOrders,
    isOpeningOrder,
    isClosingOrder,
    selectedOrderTableId,
    selectedOrderTableLabel,
    selectedOrderId,
    selectedOrder,
    isPaymentScreenOpen,
    isReceiptScreenOpen,
    isSavingGuestName,
    paymentlines,
    posTerminals,
    setPosTerminals,
    isLoadingPaymentlines,
    isPreparingPaymentline,
    isMarkingPaymentDone,
    isSplittingPayment,
    isSettingTip,
    paymentOveragePrompt,
    paymentZeroTotalPrompt,
    isResolvingPaymentOverage,
    isResolvingZeroTotalClose,
    failedCreditPaymentlineIds,
    orderlines,
    isLoadingOrderlines,
    orderlinesLoadedOrderId,
    isAddingOrderline,
    isAdjustingOrderline,
    isSendingToKitchen,
    posProducts,
    isLoadingPosProducts,
    posCategories,
    setPosCategories,
    productSearch,
    setProductSearch,
    recentResultIds,
    recentResultsVersion,
    clearRecentResults,
    setSelectedOrderTableId,
    setSelectedOrderTableLabel,
    setSelectedOrderId,
    handleOpenTakeAway,
    setIsPaymentScreenOpen,
    setIsReceiptScreenOpen,
    setPaymentlines,
    setOrderlines,
    setActiveOrders,
    setPosProducts,
    handleUpdateDiners,
    handleToggleOrderDeposit,
    handleToggleOrderVip,
    isUpdatingDiners,
    handleOpenOrderWithDiners,
    handleCloseOrder,
    handleOpenPaymentScreen,
    applyQuickChargeAmount,
    cancelQuickCharge,
    clearQuickChargeAmountPrompt,
    handleOpenReceiptScreen,
    handleSaveGuestName,
    handlePayAndCloseIfDone,
    handleSettleCashOverage,
    handleRevertCashPayment,
    dismissPaymentOverageToMenu,
    confirmPaymentOverageClose,
    cashDrawerPrompt,
    confirmCashDrawerSelection,
    dismissCashDrawerPrompt,
    dismissZeroTotalToMenu,
    confirmZeroTotalClose,
    handleChargeCredit,
    handleChargeManualCredit,
    handleChargeTapOnPhone,
    handleCreditTransactionResolved,
    shouldDeferOrderClosedCleanup,
    handleCreditTransactionPending,
    handleTapOnPhonePending,
    handleTapOnPhoneReleased,
    lockedCreditPaymentlineIds,
    handleSplitPaymentline,
    handleSplitAndPay,
    handleSplitAndPayTapOnPhone,
    handleSetOrderTip,
    handleSetPaymentlineTip,
    handleRecoverFailedCreditPaymentline,
    handleResetOrderTipAndLines,
    handleAddOrderline,
    handleAddConfiguredOrderline,
    handleIncrementOrderline,
    handleDecrementOrderline,
    handleUpdateOrderlineSelections,
    handleUpdateOrderlineNote,
    handleSetOrderlineQuantity,
    handleSendToKitchen,
    clearOrderSelection,
    resetForEmployeeSwitch,
    loadMergeLinks,
    activeOrdersRef,
    selectedOrderIdRef,
    initialOrdersLoadedForSessionRef,
    transferMode,
    isTransferring,
    setIsTransferring,
    handleBeginTransfer,
    handleTransferItemsSelected,
    handleCancelTransfer,
    handleExecuteTransferFull,
    mergeMode,
    activeMergeLinks,
    handleBeginMerge,
    handleCancelMerge,
    handleToggleMergeTable,
    handleConfirmMerge,
    splitMergeOrderPk,
    isSplittingMerge,
    handleBeginSplitMerge,
    handleCancelSplitMerge,
    handleConfirmSplitMerge,
    mergeConflict,
    handleMergeConflictCancel,
    handleMergeConflictConfirm,
    transferMutationInFlightRef,
    refreshOrderlinesForCurrentOrder,
    orderlinesRef,
    getBillableTotalForOrder,
  } = usePosOrdersDomain({
    tenantId,
    employeeSession,
    activeSession,
    setError,
    onAfterFullPayment: () => setActiveTab(landingTabRef.current),
    onNeedDinersForMerge: (centralTableId: number, centralTableLabel: string) => {
      setSelectedOrderTableId(centralTableId);
      setSelectedOrderTableLabel(centralTableLabel);
      pendingMergeAfterDinersRef.current = true;
      setIsDinersNumPadOpen(true);
    },
    onReceiptAutoPrint: requestReceiptAutoPrint,
    onTakeAwayKitchenPrint: (order, lines, products) =>
      requestTakeAwayKitchenPrint(order, lines, products, posCategories, resolveComboSelectionRoute),
  });
  selectedOrderIdForApprovalRef.current = selectedOrderId;

  const handleGreenFireRescueRequested = useCallback(
    (event: Extract<PosStreamEvent, { type: 'green_fire.rescue.requested' }>) => {
      if (!employeeSession || !activeSessionId) {
        return;
      }
      if (event.targetSessionId && event.targetSessionId !== activeSessionId) {
        return;
      }
      void (async () => {
        try {
          const claim = await kdsService.claimGreenFireRescue(tenantId, event.rescueId, employeeSession);
          if (!claim.claimed) {
            return;
          }

          const order = activeOrdersRef.current.find((candidate) => candidate.id === event.orderId);
          if (!order) {
            return;
          }

          const rescueGroup: KitchenPrintGroup = {
            printerId: event.printerId,
            orderlineIds: event.orderlineIds,
            hasKdsLines: false,
            logItems: event.lines.map((line) => ({
              name: line.productName,
              quantity: line.quantity,
              isKds: false,
            })),
            groupLines: event.lines.map((line) => ({
              id: line.id,
              productName: line.productName,
              quantity: line.quantity,
              note: null,
              selectionGroups: [],
            })),
          };

          await requestKitchenFireDispatch({
            order,
            groups: [rescueGroup],
            actionGroupId: event.actionGroupId,
            ticket: {
              tableNumber: event.tableNumber,
              orderNumber: event.orderNumber,
              dinersCount: event.dinersCount,
              waiterName: event.waiterName,
              guestName: order.guestName ?? undefined,
              badge: event.badge,
            },
          });
        } catch {
        }
      })();
    },
    [employeeSession, activeSessionId, tenantId, activeOrdersRef, requestKitchenFireDispatch],
  );

  const {
    paidActive,
    paidOrders,
    isLoadingPaid,
    togglePaid,
    onOrderClosed: onPaidOrderClosed,
    onOrderUpserted: onPaidOrderUpserted,
  } = useActiveSessionPaidOrders({
    tenantId,
    employeeSession,
    activeSession,
    isOrdersView: activeTab === 'orders',
    setError,
  });

  // Manual (card-not-present) credit: the menu picks a terminal/pinpad, then this target drives the card-entry popup.
  // splitTotal set = partial tender (split-and-pay total T); undefined = pay the whole line.
  const [manualCreditTarget, setManualCreditTarget] = useState<{
    orderId: number;
    paymentlineId: number;
    terminalId: number;
    pinpadId: string;
    splitTotal?: number;
    lineFullTotal?: number;
  } | null>(null);

  const handleOpenManualCredit = useCallback(
    (
      orderId: number,
      paymentlineId: number,
      terminalId: number,
      pinpadId: string,
      splitTotal?: number,
      lineFullTotal?: number,
    ) => {
      if (manualCreditEntryBlocked) {
        notifications.show({
          color: 'orange',
          title: 'אשראי ידני',
          message: 'האופציה חסומה',
          autoClose: 3000,
        });
        return;
      }
      setManualCreditTarget({ orderId, paymentlineId, terminalId, pinpadId, splitTotal, lineFullTotal });
    },
    [manualCreditEntryBlocked],
  );

  const handleSubmitManualCredit = useCallback(
    async (card: { cardNumber: string; expDateMMYY: string; cvv: string; idNumber?: string | null }) => {
      if (!manualCreditTarget) {
        return;
      }
      const accepted = manualCreditTarget.splitTotal != null
        ? await handleSplitAndPay(manualCreditTarget.orderId, manualCreditTarget.paymentlineId, {
          tender: 'manual',
          amount: manualCreditTarget.splitTotal,
          terminalId: manualCreditTarget.terminalId,
          pinpadId: manualCreditTarget.pinpadId,
          ...card,
        })
        : await handleChargeManualCredit(manualCreditTarget.paymentlineId, {
          terminalId: manualCreditTarget.terminalId,
          pinpadId: manualCreditTarget.pinpadId,
          ...card,
        });
      if (accepted) {
        setManualCreditTarget(null);
      }
    },
    [handleChargeManualCredit, handleSplitAndPay, manualCreditTarget],
  );

  // Tap-on-phone: the picker shows only when the native reader (or sim) is reachable. The host can inject the bridge after boot, so availability is recomputed on mount, focus/visibility, and a short startup poll.
  const [isTapOnPhoneAvailable, setIsTapOnPhoneAvailable] = useState<boolean>(() => tapOnPhoneBridge.isAvailable());
  const [tapOnPhoneTarget, setTapOnPhoneTarget] = useState<{ paymentlineId: number } | null>(null);

  const resolvePaymentlineChargeAmount = useCallback((paymentlineId: number): number => {
    const paymentline = paymentlines.find((line) => line.id === paymentlineId);
    if (!paymentline) {
      return 0;
    }
    const visibleLines = paymentlines.filter(
      (line) => line.status === 'new' || (line.status === 'done' && !line.canceledAt),
    );
    const billableBase = roundMoney(visibleLines.reduce((sum, line) => sum + line.amount, 0));
    const globalTipTotal = resolveTip(
      selectedOrder?.tipInputMode ?? null,
      selectedOrder?.tipInputValue ?? null,
      billableBase,
    );
    const doneTipCollected = roundMoney(
      visibleLines
        .filter((line) => line.status === 'done' && !line.canceledAt)
        .reduce((sum, line) => sum + (line.globalTipCollected ?? 0), 0),
    );
    const openLines = visibleLines.filter((line) => line.status === 'new').sort((left, right) => left.id - right.id);
    const globalShares = allocateGlobalTip(
      roundMoney(globalTipTotal - doneTipCollected),
      openLines.map((line) => line.amount),
    );
    const openIndex = openLines.findIndex((line) => line.id === paymentlineId);
    const globalShare = openIndex >= 0 ? (globalShares[openIndex] ?? 0) : 0;
    const lineTip = resolveTip(paymentline.tipInputMode ?? null, paymentline.tipInputValue ?? null, paymentline.amount);
    return roundMoney(paymentline.amount + lineTip + globalShare);
  }, [paymentlines, selectedOrder?.tipInputMode, selectedOrder?.tipInputValue]);

  useEffect(() => {
    const recompute = () => setIsTapOnPhoneAvailable(tapOnPhoneBridge.isAvailable());
    recompute();
    window.addEventListener('focus', recompute);
    document.addEventListener('visibilitychange', recompute);
    let polls = 0;
    const interval = window.setInterval(() => {
      polls += 1;
      recompute();
      if (tapOnPhoneBridge.isAvailable() || polls >= 8) {
        window.clearInterval(interval);
      }
    }, 1500);
    return () => {
      window.removeEventListener('focus', recompute);
      document.removeEventListener('visibilitychange', recompute);
      window.clearInterval(interval);
    };
  }, []);

  const handleOpenTapOnPhone = useCallback((_orderId: number, paymentlineId: number) => {
    // The visible action always opens the tap-card dialog. The charge flow performs the
    // authoritative bridge check and reports an unavailable reader inside that dialog.
    setIsTapOnPhoneAvailable(tapOnPhoneBridge.isAvailable());
    setTapOnPhoneTarget({ paymentlineId });
  }, []);

  const handleStartTapOnPhone = useCallback(async (): Promise<boolean> => {
    if (!tapOnPhoneTarget) {
      return false;
    }
    return handleChargeTapOnPhone(tapOnPhoneTarget.paymentlineId);
  }, [handleChargeTapOnPhone, tapOnPhoneTarget]);

  const {
    selectionModal,
    handleProductTap,
    handleEditSelections,
    handleConfirmSelections,
    closeSelectionModal,
    invalidateComposition,
    resolveComboSelectionRoute,
  } = useVariantComboSelection({
    tenantId,
    employeeSession,
    posProducts,
    orderlines,
    addOrderline: handleAddOrderline,
    addConfiguredOrderline: handleAddConfiguredOrderline,
    updateOrderlineSelections: handleUpdateOrderlineSelections,
  });

  const {
    optionsProduct,
    variantComboCountByProductId,
    comboItemsByProductId,
    isCopyingGroup,
    reloadVariantComboCounts,
    openProductOptions,
    closeProductOptions,
    handleSaveProductOptions,
    handleCopyGroupToProducts,
    applyVariantComboGroupUpserted,
    applyVariantComboGroupRemoved,
  } = useProductOptions({
    employeeSession,
    settingsModalKey,
    composition,
    invalidateComposition,
  });

  const {
    selectedTestPrinterId,
    setSelectedTestPrinterId,
    loadSettingsEmployees,
    loadSettingsAreas,
    loadSettingsProducts,
    loadActivePrinters,
    loadPrintJobs,
    resetForEmployeeSwitch: resetSettingsBootstrapForEmployeeSwitch,
  } = usePosSettingsBootstrap({
    tenantId,
    employeeSession,
    isManager,
    isSettingsActive,
    settingsModalKey,
    activePrinters,
    setError,
    setSettingsEmployees,
    setSettingsAreas,
    setSelectedAreaId,
    setIsLoadingSettings,
    setIsLoadingAreaSettings,
    setIsLoadingProducts,
    setSettingsProducts,
    setIsLoadingPrinters,
    setActivePrinters,
    setIsLoadingPrintJobs,
    setPrintJobs,
  });

  const dailyBrief = useDailyBriefController({
    tenantId,
    employeeSession,
    isManager,
    onError: setError,
  });

  // Active+enabled printer ids for kitchen routing; inactive printers are never print targets.
  const activePrinterIds = useMemo(() => activePrinters.map((printer) => printer.id), [activePrinters]);

  const { fixedPrinterId, setFixedPrinter, clearFixedPrinter } = useFixedPrinter();
  // Subtitle/glow reflect only a still-valid active cash-drawer printer (stale id => off).
  const fixedPrinterName = useMemo(() => {
    if (fixedPrinterId == null) return null;
    const match = activePrinters.find((p) => p.id === fixedPrinterId && p.active && p.cashDrawer);
    return match ? match.name : null;
  }, [fixedPrinterId, activePrinters]);

  // Summary: Loads active employees for the POS identification gate.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const loadAvailableEmployees = useCallback(async () => {
    setIsLoadingEmployees(true);
    setError(null);
    try {
      const list = await employeesService.listActive(tenantId);
      setAvailableEmployees(list);
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      } else {
        setError('Failed loading employees');
      }
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [tenantId]);

  // Summary: Loads active areas for floor browsing in POS.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const loadActiveAreas = useCallback(async () => {
    setIsLoadingAreas(true);
    setError(null);

    try {
      const list = sortAreasForOperations(
        (await areasService.listActive(tenantId)).map((area) => normalizeArea(area)),
      );
      setActiveAreas(list);
      setSelectedAreaId((previous) => {
        if (previous !== null && list.some((area) => area.id === previous)) {
          return previous;
        }
        return list[0]?.id ?? null;
      });
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      } else {
        setError('Failed loading areas');
      }
    } finally {
      setIsLoadingAreas(false);
    }
  }, [tenantId]);

  // Summary: Loads the active tenant session for current employee gating.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const loadActiveSession = useCallback(async () => {
    if (!employeeSession) {
      setActiveSession(null);
      setNextOpeningCashSuggestion(0);
      setGeneralItemRequiresManagerApproval(true);
      setShiftCloseCashBalancesEnabled(false);
      setOrdersPageWaiterOwnVisibility(false);
      setManualCreditEntryBlocked(false);
      setCustomerNameOnOrderEntry(false);
      setStationWaiterSelectionEnabled(false);
      setCashCloseManagerOnly(false);
      setEmployeeShiftCloseEnabled(false);
      setAreaPrintRules([]);
      setRefundCodeViaPush(false);
      setEmployeeAttendanceReportEnabled(false);
      setAttendanceUiEnabled(true);
      setAttendanceReportRules(DEFAULT_ATTENDANCE_REPORT_RULES);
      return;
    }

    setIsLoadingSession(true);
    try {
      const active = await sessionsService.getActive(tenantId, employeeSession);
      setActiveSession(active.session);
      setNextOpeningCashSuggestion(active.nextOpeningCashSuggestion ?? 0);
      setAutoCloseTime(active.autoCloseTime);
      setFloorBackground(active.floorBackground);
      setRestigoEnabled(active.restigoEnabled);
      setGeneralItemRequiresManagerApproval(active.generalItemRequiresManagerApproval ?? true);
      setShiftCloseCashBalancesEnabled(active.shiftCloseCashBalancesEnabled ?? false);
      setOrdersPageWaiterOwnVisibility(active.ordersPageWaiterOwnVisibility ?? false);
      setManualCreditEntryBlocked(active.manualCreditEntryBlocked ?? false);
      setCustomerNameOnOrderEntry(active.customerNameOnOrderEntry ?? false);
      setStationWaiterSelectionEnabled(active.stationWaiterSelectionEnabled ?? false);
      setCashCloseManagerOnly(active.cashCloseManagerOnly ?? false);
      setEmployeeShiftCloseEnabled(active.employeeShiftCloseEnabled ?? false);
      setAreaPrintRules(active.areaPrintRules ?? []);
      setRefundCodeViaPush(active.refundCodeViaPush ?? false);
      setEmployeeAttendanceReportEnabled(active.employeeAttendanceReportEnabled ?? false);
      setAttendanceUiEnabled(active.attendanceUiEnabled ?? true);
      setAttendanceReportRules(active.attendanceReportRules ?? DEFAULT_ATTENDANCE_REPORT_RULES);
      setTenantIsTest(active.isTest ?? false);
    } catch (sessionError: unknown) {
      if (sessionError instanceof Error) {
        setError(sessionError.message);
      } else {
        setError('Failed loading active session');
      }
    } finally {
      setIsLoadingSession(false);
    }
  }, [employeeSession, tenantId]);

  // Summary: Manager picks the floor-plan canvas background (עריכת אזורים); saved tenant-wide, rides SSE to every device.
  const floorBackgroundInFlightRef = useRef(false);
  const handleFloorBackgroundChange = useCallback(
    async (next: 'white' | 'image' | 'primary') => {
      if (!employeeSession || floorBackgroundInFlightRef.current) {
        return;
      }
      floorBackgroundInFlightRef.current = true;
      try {
        await sessionsService.updateFloorBackground(tenantId, next, employeeSession);
      } catch (backgroundError: unknown) {
        setError(backgroundError instanceof Error ? backgroundError.message : 'Failed updating floor background');
      } finally {
        floorBackgroundInFlightRef.current = false;
      }
    },
    [employeeSession, tenantId],
  );

  const handleGeneralItemApprovalSettingChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateGeneralItemApprovalRequired(tenantId, next, employeeSession);
      setGeneralItemRequiresManagerApproval(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleShiftCloseCashBalancesSettingChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) return;
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateShiftCloseCashBalancesEnabled(tenantId, next, employeeSession);
      setShiftCloseCashBalancesEnabled(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleAttendanceUiEnabledChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) return;
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateAttendanceUiEnabled(tenantId, next, employeeSession);
      setAttendanceUiEnabled(next);
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleRefundCodeViaPushChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateRefundCodeViaPush(tenantId, next, employeeSession);
      setRefundCodeViaPush(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleOrdersPageWaiterOwnVisibilityChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateOrdersPageWaiterOwnVisibility(tenantId, next, employeeSession);
      setOrdersPageWaiterOwnVisibility(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleManualCreditEntryBlockedChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateManualCreditEntryBlocked(tenantId, next, employeeSession);
      setManualCreditEntryBlocked(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleCustomerNameOnOrderEntryChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateCustomerNameOnOrderEntry(tenantId, next, employeeSession);
      setCustomerNameOnOrderEntry(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleStationWaiterSelectionEnabledChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateStationWaiterSelectionEnabled(tenantId, next, employeeSession);
      setStationWaiterSelectionEnabled(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleCashCloseManagerOnlyChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateCashCloseManagerOnly(tenantId, next, employeeSession);
      setCashCloseManagerOnly(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleEmployeeShiftCloseEnabledChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateEmployeeShiftCloseEnabled(tenantId, next, employeeSession);
      setEmployeeShiftCloseEnabled(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const [isSavingAreaPrintRules, setIsSavingAreaPrintRules] = useState(false);
  const handleSaveAreaPrintRules = useCallback(async (rules: AreaPrintRule[]): Promise<boolean> => {
    if (!employeeSession || isSavingAreaPrintRules) return false;
    setIsSavingAreaPrintRules(true);
    try {
      await sessionsService.updateAreaPrintRules(tenantId, rules, employeeSession);
      setAreaPrintRules(rules);
      notifications.show({
        color: 'green',
        title: 'הדפסה אזורית',
        message: 'ההגדרות נשמרו בהצלחה',
        autoClose: 3000,
      });
      return true;
    } catch (err: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
        autoClose: 3000,
      });
      return false;
    } finally {
      setIsSavingAreaPrintRules(false);
    }
  }, [employeeSession, isSavingAreaPrintRules, tenantId]);

  const handleEmployeeAttendanceReportEnabledChange = useCallback(async (next: boolean) => {
    if (!employeeSession || isSavingPosSettings || posSettingsSaveInFlightRef.current) {
      return;
    }
    posSettingsSaveInFlightRef.current = true;
    setIsSavingPosSettings(true);
    try {
      await sessionsService.updateEmployeeAttendanceReportEnabled(tenantId, next, employeeSession);
      setEmployeeAttendanceReportEnabled(next);
    } catch (settingsError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(settingsError instanceof Error ? settingsError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingPosSettings(false);
      posSettingsSaveInFlightRef.current = false;
    }
  }, [employeeSession, isSavingPosSettings, tenantId]);

  const handleAttendanceReportRulesSave = useCallback(async (next: AttendanceReportRules) => {
    if (!employeeSession || isSavingAttendanceRules) {
      return;
    }
    setIsSavingAttendanceRules(true);
    try {
      await sessionsService.updateAttendanceReportRules(tenantId, next, employeeSession);
      setAttendanceReportRules(next);
      setSettingsModalKey(null);
      notifications.show({
        color: 'green',
        title: 'נוכחות',
        message: 'כללי הדוח נשמרו',
        autoClose: 2500,
      });
    } catch (rulesError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שמירת ההגדרה נכשלה',
        message: toHebrewApiErrorMessage(rulesError instanceof Error ? rulesError.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSavingAttendanceRules(false);
    }
  }, [employeeSession, isSavingAttendanceRules, tenantId]);

  // Summary: One-shot Z-report figures fetch when the close-session modal opens; in-flight ref prevents duplicate GETs on re-render (Rule 21/28).
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const isLoadingSummaryRef = useRef(false);
  useEffect(() => {
    if (settingsModalKey !== 'session' || !employeeSession) {
      return;
    }
    if (isLoadingSummaryRef.current) {
      return;
    }
    isLoadingSummaryRef.current = true;
    void (async () => {
      try {
        const summary = await sessionsService.getSummary(tenantId, employeeSession);
        setSessionSummary(summary);
      } catch {
        setSessionSummary({ orderCount: 0, salesTotal: 0 });
      } finally {
        isLoadingSummaryRef.current = false;
      }
    })();
  }, [settingsModalKey, employeeSession, tenantId]);

  useEffect(() => {
    void loadAvailableEmployees();
    const restored = hydrateEmployeeState(tenantId);
    if (!restored) {
      setActiveTab(landingTabRef.current);
      setAreaMode('view');
    }
  }, [hydrateEmployeeState, loadAvailableEmployees, setAreaMode, tenantId]);

  // Summary: Defers area loading until employee login (areas are not needed on picker screen).
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  useEffect(() => {
    if (!employeeSession) {
      return;
    }
    void loadActiveAreas();
  }, [employeeSession, loadActiveAreas]);

  // Summary: Subscribes picker view to employee SSE updates before login.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  useEffect(() => {
    if (employeeSession || !tenantId) {
      return;
    }
    if (typeof window.EventSource !== 'function') {
      return;
    }

    const unsubscribe = subscribePickerEmployeesEvents(tenantId, (event) => {
      if (event.type === 'employee.upserted') {
        const incoming = event.employee as EmployeeView;
        setAvailableEmployees((previous) =>
          incoming.isActive ? mergeById(previous, incoming) : removeById(previous, incoming.id),
        );
        return;
      }
      if (event.type === 'employee.deactivated') {
        setAvailableEmployees((previous) => removeById(previous, event.employeeId));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [employeeSession, tenantId]);

  useEffect(() => {
    if (!employeeSession) {
      setActiveSession(null);
      return;
    }

    void loadActiveSession();
  }, [employeeSession, loadActiveSession]);

  // Summary: Identifies employee and persists tenant-scoped POS employee session.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const handleBeforeSelectEmployee = async (employee: { id: number; fullName: string }): Promise<boolean> => {
    logEmployeeSessionAudit('picker_session_status_check_started', { tenantId, employeeId: employee.id });
    const status = await employeesService.checkSession(tenantId, employee.id);
    if (status.occupied && status.existingSessionId) {
      logEmployeeSessionAudit('picker_session_status_conflict', {
        tenantId,
        employeeId: employee.id,
        existingSessionId: employeeSessionFingerprint(status.existingSessionId),
      });
      setTakeoverConflict({ employeeId: employee.id, employeeName: employee.fullName, existingSessionId: status.existingSessionId });
      return false;
    }
    logEmployeeSessionAudit('picker_session_status_available', { tenantId, employeeId: employee.id });
    return true;
  };

  const handleIdentifyDirect = async (employeeId: number, candidatePin: string): Promise<boolean> => {
    logEmployeeSessionAudit('picker_identify_started', { tenantId, employeeId });
    setIsIdentifying(true);
    setError(null);
    try {
      const result = await employeesService.identify(tenantId, {
        employeeId,
        pin: candidatePin,
      });
      if (result.conflict) {
        logEmployeeSessionAudit('picker_identify_conflict', {
          tenantId,
          employeeId,
          existingSessionId: employeeSessionFingerprint(result.conflict.existingSessionId),
        });
        const employeeName = availableEmployees.find((e) => e.id === employeeId)?.fullName ?? '';
        setTakeoverConflict({ employeeId, employeeName, existingSessionId: result.conflict.existingSessionId });
        return false;
      }
      setEmployeeState(tenantId, result);
      setActiveTab(landingTabRef.current);
      logEmployeeSessionAudit('picker_identify_succeeded', {
        tenantId,
        employeeId,
        activeSessionId: employeeSessionFingerprint(result.activeSessionId),
      });
      return true;
    } catch (identifyError: unknown) {
      logEmployeeSessionAudit('picker_identify_failed', {
        tenantId,
        employeeId,
        failure: identifyError instanceof Error ? identifyError.message : typeof identifyError,
      });
      setError(toHebrewApiErrorMessage(identifyError instanceof Error ? identifyError.message : ''));
      return false;
    } finally {
      setIsIdentifying(false);
    }
  };

  const printPendingAttendanceAction = useCallback(async (printerId: number) => {
    if (!pendingAttendancePrintAction || !attendancePrintRef.current || attendancePrintInFlightRef.current) {
      return;
    }
    attendancePrintInFlightRef.current = true;
    try {
      const image = await captureReceiptSnapshot(attendancePrintRef.current);
      if (!image) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'לא ניתן ליצור תמונה להדפסה', autoClose: 3000 });
        return;
      }
      await attendanceService.printSlip(tenantId, printerId, image, pendingAttendancePrintAction.employeeId);
      notifications.show({ color: 'green', title: 'נוכחות', message: 'אישור הנוכחות נשלח להדפסה', autoClose: 3000 });
      setIsAttendancePrintPromptOpen(false);
      setAttendancePrintCandidates(null);
      setPendingAttendancePrintAction(null);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'שגיאה לא ידועה',
        autoClose: 3000,
      });
    } finally {
      attendancePrintInFlightRef.current = false;
    }
  }, [pendingAttendancePrintAction, tenantId]);

  const openAttendancePrintSelection = useCallback(async () => {
    try {
      const printers = await attendanceService.listBillablePrinters(tenantId);
      if (printers.length === 0) {
        notifications.show({ color: 'orange', title: 'נוכחות', message: 'אין מדפסות חשבונות פעילות', autoClose: 3000 });
        setPendingAttendancePrintAction(null);
        return;
      }
      if (printers.length === 1) {
        await printPendingAttendanceAction(printers[0].id);
        return;
      }
      setAttendancePrintCandidates(printers);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'טעינת המדפסות נכשלה',
        autoClose: 3000,
      });
      setPendingAttendancePrintAction(null);
    }
  }, [printPendingAttendanceAction, tenantId]);

  const handleCancelAttendancePrintPrompt = useCallback(() => {
    if (isResolvingAttendancePrintChoice || attendancePrintInFlightRef.current) {
      return;
    }
    setIsAttendancePrintPromptOpen(false);
    setAttendancePrintCandidates(null);
    setPendingAttendancePrintAction(null);
  }, [isResolvingAttendancePrintChoice]);

  const handleConfirmAttendancePrintPrompt = useCallback(async () => {
    if (isResolvingAttendancePrintChoice || attendancePrintInFlightRef.current) {
      return;
    }
    setIsResolvingAttendancePrintChoice(true);
    setIsAttendancePrintPromptOpen(false);
    try {
      await openAttendancePrintSelection();
    } finally {
      setIsResolvingAttendancePrintChoice(false);
    }
  }, [isResolvingAttendancePrintChoice, openAttendancePrintSelection]);

  const handleAttendanceActionFromGate = useCallback(async (
    employeeId: number,
    pin: string,
    actionType: AttendanceActionType,
  ): Promise<boolean> => {
    setIsAttendanceActioning(true);
    setError(null);
    try {
      const action = await attendanceService.clockAction(tenantId, { employeeId, pin, actionType });
      setPendingAttendancePrintAction(action);
      notifications.show({
        color: 'green',
        title: 'נוכחות',
        message: actionType === 'clock_in' ? 'הכניסה נשמרה בהצלחה' : 'היציאה נשמרה בהצלחה',
        autoClose: 3000,
      });
      setIsAttendancePrintPromptOpen(true);
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'אירעה שגיאה. נסו שוב.';
      setError(message);
      return false;
    } finally {
      setIsAttendanceActioning(false);
    }
  }, [tenantId]);

  const handleResolveAttendanceShiftStatus = useCallback(async (employeeId: number) => {
    return attendanceService.getEmployeeShiftStatus(tenantId, employeeId);
  }, [tenantId]);

  const handleTakeoverConfirm = () => {
    logEmployeeSessionAudit('picker_takeover_confirmed', {
      tenantId,
      employeeId: takeoverConflict?.employeeId ?? null,
      existingSessionId: employeeSessionFingerprint(takeoverConflict?.existingSessionId),
    });
    setTakeoverPinOpen(true);
  };

  const handleTakeoverPinSubmit = async (pin: string): Promise<boolean> => {
    if (!takeoverConflict || takeoverInFlightRef.current) return false;
    logEmployeeSessionAudit('picker_takeover_started', {
      tenantId,
      employeeId: takeoverConflict.employeeId,
      existingSessionId: employeeSessionFingerprint(takeoverConflict.existingSessionId),
    });
    takeoverInFlightRef.current = true;
    setIsTakingOver(true);
    try {
      const result = await employeesService.takeover(tenantId, {
        employeeId: takeoverConflict.employeeId,
        pin,
        existingSessionId: takeoverConflict.existingSessionId,
      });
      setTakeoverPinOpen(false);
      setTakeoverConflict(null);
      setEmployeeState(tenantId, result);
      setActiveTab(landingTabRef.current);
      logEmployeeSessionAudit('picker_takeover_succeeded', {
        tenantId,
        employeeId: result.employee.id,
        activeSessionId: employeeSessionFingerprint(result.activeSessionId),
      });
      return true;
    } catch (takeoverError: unknown) {
      logEmployeeSessionAudit('picker_takeover_failed', {
        tenantId,
        employeeId: takeoverConflict.employeeId,
        failure: takeoverError instanceof Error ? takeoverError.message : typeof takeoverError,
      });
      setError(toHebrewApiErrorMessage(takeoverError instanceof Error ? takeoverError.message : ''));
      return false;
    } finally {
      setIsTakingOver(false);
      takeoverInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (activeTab === 'areas' && areaMode === 'edit' && isManager && employeeSession) {
      void loadSettingsAreas();
    }
  }, [activeTab, areaMode, employeeSession, isManager, loadSettingsAreas]);

  // Summary: Clears employee-scoped POS state and returns to identification gate.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const handleSwitchEmployee = useCallback(() => {
    logEmployeeSessionAudit('pos_switch_to_picker_started', {
      tenantId,
      activeSessionId: employeeSessionFingerprint(activeSessionId),
      hasEmployeeSession: Boolean(employeeSession),
    });
    if (employeeSession && activeSessionId) {
      void employeesService.clearActiveSession(tenantId, employeeSession, activeSessionId);
    }
    clearEmployeeState(tenantId);
    setActiveTab(landingTabRef.current);
    setAreaMode('view');
    setSelectedAreaId(null);
    setEditingTableId(null);
    setTableForm(INITIAL_TABLE_FORM);
    setSettingsEmployees([]);
    setSettingsProducts([]);
    setActivePrinters([]);
    setPrintJobs([]);
    updatingProductKdsIdsRef.current.clear();
    setUpdatingProductKdsIds([]);
    setEditingProductId(null);
    setProductFormRows([createProductFormRow()]);
    setEditingProductBaseline(null);
    setEditingProductAreaPrintRuleId(null);
    setEditingEmployeeId(null);
    setEditingEmployeeBaseline(null);
    setActiveSession(null);
    setGeneralItemRequiresManagerApproval(true);
    setOrdersPageWaiterOwnVisibility(false);
    setManualCreditEntryBlocked(false);
    setCustomerNameOnOrderEntry(false);
    setStationWaiterSelectionEnabled(false);
    stationWaiter.clearActingWaiter();
    setCashCloseManagerOnly(false);
    setEmployeeShiftCloseEnabled(false);
    setAreaPrintRules([]);
    setRefundCodeViaPush(false);
    setEmployeeAttendanceReportEnabled(false);
    setAttendanceReportRules(DEFAULT_ATTENDANCE_REPORT_RULES);
    resetForEmployeeSwitch();
    resetSettingsBootstrapForEmployeeSwitch();
    setForm(INITIAL_FORM);
    setError(null);
    logEmployeeSessionAudit('pos_switch_to_picker_completed', { tenantId });
  }, [
    activeSessionId,
    clearEmployeeState,
    employeeSession,
    resetForEmployeeSwitch,
    resetSettingsBootstrapForEmployeeSwitch,
    setAreaMode,
    tenantId,
  ]);

  // Summary: Enforces session-open gating on table click in view mode.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
  const continueTableClick = useCallback((tableId: number, tableLabel: string) => {
    if (stationWaiter.intercept({ kind: 'table', tableId, tableLabel })) {
      return;
    }
    const mergedOrder = activeMergeLinks
      .find((link) => link.tablePk === tableId)
      ?.orderPk;
    const resolvedOrder = mergedOrder
      ? activeOrders.find((order) => order.id === mergedOrder) ?? null
      : null;
    const resolvedTableId = resolvedOrder?.tableId ?? tableId;
    const resolvedTableLabel = resolvedOrder?.tableNumber ?? tableLabel;

    setSelectedOrderId(null);
    setSelectedOrderTableId(resolvedTableId);
    setSelectedOrderTableLabel(resolvedTableLabel);
    setIsGuestNameEditOpen(false);

    const isOccupied = resolvedOrder !== null || activeOrders.some((o) => o.tableId === tableId);
    if (isOccupied) {
      setActiveTab('orders');
    } else {
      setIsDinersNumPadOpen(true);
    }
  }, [
    activeOrders,
    activeMergeLinks,
    stationWaiter.intercept,
    setSelectedOrderId,
    setSelectedOrderTableId,
    setSelectedOrderTableLabel,
  ]);

  const openSessionFlow = useOpenSessionFlow({
    tenantId,
    employeeSession,
    setActiveSession,
    setError,
    onSessionOpened: (pending) => {
      if ('orderVariant' in pending) {
        void (pending.orderVariant === 'kiosk_dine_in' ? handleOpenKioskDineIn() : handleOpenTakeAwayClick());
        return;
      }
      continueTableClick(pending.tableId, pending.tableLabel);
    },
  });
  requestEditOpeningCashRef.current = openSessionFlow.requestEditOpeningCash;

  const closeOpenSessionModal = openSessionFlow.closeModal;
  const handleForeignSessionOpen = useCallback(() => {
    notifications.show({
      color: 'orange',
      title: 'המשמרת נפתחה',
      message: 'המשמרת נפתחה ממכשיר אחר',
      autoClose: 3000,
    });
    closeOpenSessionModal();
  }, [closeOpenSessionModal]);

  useForeignSessionOpenNotice({
    isOpenModalActive: openSessionFlow.modalMode === 'open',
    isSaving: openSessionFlow.isSaving,
    hasActiveSession: activeSession !== null,
    onForeignOpen: handleForeignSessionOpen,
  });

  const handleViewTableClick = useCallback((tableId: number, tableLabel: string) => {
    if (!employeeSession) {
      return;
    }

    if (!activeSession) {
      openSessionFlow.requestOpenSession({ tableId, tableLabel });
      return;
    }

    continueTableClick(tableId, tableLabel);
  }, [activeSession, continueTableClick, employeeSession, openSessionFlow]);

  // State (not a ref) so the shell re-renders and the safe-zone gate reopens when the Z print ends.
  const [isAutoZPrintInFlight, setIsAutoZPrintInFlight] = useState(false);

  // Shared defer-until-safe gate + manager broadcast, composed off-file in usePosRefresh.
  const { requestRefresh } = usePosRefresh({
    tenantId,
    employeeSession,
    isManager,
    broadcastInFlightRef: broadcastRefreshInFlightRef,
    requestRefreshRef,
    broadcastRefreshRef,
    safeZone: {
      activeTab,
      areaMode,
      selectedOrderId,
      isPaymentScreenOpen,
      isReceiptScreenOpen,
      isPostPaymentScreenOpen,
      hasCashChangeDialog: isCashChangeDialogOpen,
      isGuestNameEditOpen,
      isReceiptPreviewOpen,
      isSettingTip,
      isDinersNumPadOpen,
      isSettingsSubFlowActive,
      // Hub-launched modals that open via their own flags (not settingsModalKey); all defer a refresh.
      hasHubModalOpen:
        isHappyHourSetupOpen ||
        isHappyHourActiveOpen ||
        vatEdit.isOpen ||
        accountantReportOpen ||
        reports.singleReportOpen ||
        reports.rangeMode !== null ||
        reports.zLayoutOpen ||
        openSessionFlow.modalMode !== null,
      hasManualCreditTarget: manualCreditTarget !== null,
      hasTapOnPhoneTarget: tapOnPhoneTarget !== null,
      hasSelectionModal: selectionModal !== null,
      hasPaymentOveragePrompt: paymentOveragePrompt !== null,
      hasPaymentZeroTotalPrompt: paymentZeroTotalPrompt !== null,
      hasCashDrawerPrompt: cashDrawerPrompt !== null,
      hasCancelTableConfirm: cancelTableConfirm !== null,
      transferMode: transferMode.step !== 'idle',
      mergeMode: mergeMode.step !== 'idle',
      splitMergeOrderPk,
      hasMergeConflict: mergeConflict !== null,
      // Broadcast lock intentionally excluded: only real order/table writes block a refresh.
      hasInFlightWrite:
        transferMutationInFlightRef.current ||
        tableMutationInFlightRef.current ||
        cancelTableInFlightRef.current ||
        isPersistingInteractionRef.current ||
        openDrawerInFlightRef.current ||
        closeSessionInFlightRef.current ||
      isAutoZPrintInFlight ||
      isManualZPrintInFlight,
  },
  });

// A new build found on re-open forces an immediate reload: stale assets are a correctness risk.
useAppVersionWatch(true, (version) => requestRefresh('version', version));

// Transfer ends on areas with the source still selected; clear on idle so it can't block a refresh.
useEffect(() => {
  if (transferMode.step === 'idle') {
    clearOrderSelection();
  }
}, [transferMode.step, clearOrderSelection]);

// Live auto-close → drive the shell-level Z auto-print; nonce reprints even on a repeated id.
const [autoClosedZPrint, setAutoClosedZPrint] = useState<{ sessionId: number; nonce: number }>({
  sessionId: 0,
  nonce: 0,
});
const handleSessionAutoClosed = useCallback((sessionId: number) => {
  setAutoClosedZPrint((previous) => ({ sessionId, nonce: previous.nonce + 1 }));
}, []);

// Summary: Single multiplexed POS SSE stream subscription (orders, areas, employees,
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
usePosStreamSubscription({
  tenantId,
  employeeSession,
  isManager,
  loadMergeLinks,
  transferMutationInFlightRef,
  activeOrdersRef,
  selectedOrderIdRef,
  initialOrdersLoadedForSessionRef,
  editingTableIdRef,
  setActiveOrders,
  setOrderlines,
  setPaymentlines,
  setPosProducts,
  setPosTerminals,
  setActivePrinters,
  setPosCategories,
  setSettingsCategories,
  applyVariantComboGroupUpserted,
  applyVariantComboGroupRemoved,
  setActiveSession,
  setAutoCloseTime,
  setFloorBackground,
  setGeneralItemRequiresManagerApproval,
  setOrdersPageWaiterOwnVisibility,
  setManualCreditEntryBlocked,
  setCustomerNameOnOrderEntry,
  setStationWaiterSelectionEnabled,
  setCashCloseManagerOnly,
  setEmployeeShiftCloseEnabled,
  setAreaPrintRules,
  setRefundCodeViaPush,
  setEmployeeAttendanceReportEnabled,
  setAttendanceReportRules,
  onSessionDailyBriefUpdated: dailyBrief.onUpdated,
  setActiveAreas,
  setSettingsAreas,
  setAvailableEmployees,
  setSettingsEmployees,
  setSettingsProducts,
  setHappyHour,
  setEditingTableId,
  setTableForm,
  onCreditTransactionResolved: handleCreditTransactionResolved,
  onCreditTransactionPending: handleCreditTransactionPending,
  onTapOnPhonePending: handleTapOnPhonePending,
  onTapOnPhoneReleased: handleTapOnPhoneReleased,
  onApprovalEvent: handleApprovalEvent,
  requestDeferredRefresh: requestRefresh,
  onSessionAutoClosed: handleSessionAutoClosed,
  interactionRef,
  currentEmployeeId: currentEmployee?.id ?? null,
  activeEmployeeSessionId: activeSessionId,
  onEmployeeKicked: handleSwitchEmployee,
  onOrderClosed: onPaidOrderClosed,
  onClosedOrderUpserted: onPaidOrderUpserted,
  shouldDeferOrderClosedCleanup,
  onSelectedQuickChargeClosed: () => { clearOrderSelection(); setActiveTab(landingTabRef.current); },
  onStreamConnectionLost: markStreamDown,
  onStreamActivity: markStreamAlive,
  onGreenFireRescueRequested: handleGreenFireRescueRequested,
  orderlinesRef,
  getBillableTotalForOrder,
  isPaymentScreenOpen,
});

// Summary: Closes the active session from manager settings.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleCloseSession = useCallback(async (payload?: {
  cashDrawerPrinterId?: number;
  closingCash?: number;
  nextOpeningCash?: number;
}): Promise<SessionCloseOutcome> => {
  if (!employeeSession || (!isManager && !employeeShiftCloseEnabled) || closeSessionInFlightRef.current) {
    return { status: 'error' };
  }

  if (!activeSession) {
    setError('No active session to close');
    return { status: 'error' };
  }

  closeSessionInFlightRef.current = true;
  setIsSavingSession(true);
  setError(null);
  try {
    try {
      const freshSummary = await sessionsService.getSummary(tenantId, employeeSession);
      setSessionSummary(freshSummary);
    } catch {
    }
    await sessionsService.close(tenantId, employeeSession, payload);
    setActiveSession(null);
    setNextOpeningCashSuggestion(payload?.nextOpeningCash ?? 0);
    setSettingsModalKey(null);
    notifications.show({
      color: 'green',
      message: 'קופה נסגרה בהצלחה',
      autoClose: 3000,
    });
    return { status: 'closed' };
  } catch (closeError: unknown) {
    // Orders opened mid-flow: show the open-orders modal, never a raw error or a printed Z.
    if (closeError instanceof SessionHasOpenOrdersError) {
      return { status: 'open-orders', openOrderCount: closeError.openOrderCount };
    }
    setError(closeError instanceof Error ? closeError.message : 'Failed closing session');
    return { status: 'error' };
  } finally {
    closeSessionInFlightRef.current = false;
    setIsSavingSession(false);
  }
}, [activeSession, employeeSession, isManager, employeeShiftCloseEnabled, tenantId, setSettingsModalKey]);

const handleRetroSetCountedCash = useCallback(async (sessionId: number, closingCash: number) => {
  if (!employeeSession || !isManager || retroCountedCashInFlightRef.current) {
    return;
  }

  retroCountedCashInFlightRef.current = true;
  setIsSavingRetroCountedCash(true);
  setError(null);
  try {
    await sessionsService.updateClosedCountedCashForSession(tenantId, employeeSession, sessionId, closingCash);
    setSettingsModalKey(null);
    notifications.show({
      color: 'green',
      title: 'מזומן נספר',
      message: 'העדכון נשמר בהצלחה',
      autoClose: 3000,
    });
  } catch (retroError: unknown) {
    notifications.show({
      color: 'red',
      title: 'שמירה נכשלה',
      message: toHebrewApiErrorMessage(retroError instanceof Error ? retroError.message : ''),
      autoClose: 3000,
    });
  } finally {
    retroCountedCashInFlightRef.current = false;
    setIsSavingRetroCountedCash(false);
  }
}, [employeeSession, isManager, tenantId, setSettingsModalKey]);

useEffect(() => {
  if (settingsModalKey !== 'retroCountedCash' || !employeeSession || !isManager) {
    return;
  }

  let cancelled = false;
  setIsLoadingRetroSessionOptions(true);
  void sessionsService
    .listSessionFilterOptions(tenantId, employeeSession)
    .then((options) => {
      if (cancelled) {
        return;
      }
      const closedOptions = options.filter((option) => option.state === 'closed');
      setRetroSessionOptions(closedOptions);
      setRetroSelectedSessionId((previous) => {
        if (previous != null && closedOptions.some((option) => option.id === previous)) {
          return previous;
        }
        return closedOptions[0]?.id ?? null;
      });
    })
    .catch((loadError: unknown) => {
      if (cancelled) {
        return;
      }
      setRetroSessionOptions([]);
      setRetroSelectedSessionId(null);
      notifications.show({
        color: 'red',
        title: 'טעינת משמרות נכשלה',
        message: toHebrewApiErrorMessage(loadError instanceof Error ? loadError.message : ''),
        autoClose: 3000,
      });
    })
    .finally(() => {
      if (!cancelled) {
        setIsLoadingRetroSessionOptions(false);
      }
    });

  return () => {
    cancelled = true;
  };
}, [settingsModalKey, employeeSession, isManager, tenantId]);

const retroSessionSelectData = useMemo(
  () => retroSessionOptions.map((option) => ({
    value: String(option.id),
    label: `${option.name} · ${new Date(option.date).toLocaleString('he-IL')}`,
  })),
  [retroSessionOptions],
);

// Summary: Triggers a manager-selected printer test job.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleCreatePrinterTestPrint = useCallback(async () => {
  if (!employeeSession || !isManager || selectedTestPrinterId === null) {
    return;
  }

  setIsSendingPrinterTest(true);
  setError(null);
  try {
    const result = await printersService.createTestPrint(tenantId, selectedTestPrinterId, employeeSession);
    setPrintJobs((previous) => [
      {
        id: result.job.id,
        tenantId,
        orderId: null,
        printerId: result.job.printerId,
        printerName: result.job.printerName,
        status: result.job.status,
        printableText: result.job.printableText,
        createdByEmployeeId: currentEmployee?.id ?? 0,
        claimedByEmployeeId: null,
        claimedAt: null,
        errorMessage: null,
        createdAt: result.job.createdAt,
        updatedAt: result.job.createdAt,
        completedAt: null,
      },
      ...previous,
    ]);
  } catch (testError: unknown) {
    if (testError instanceof Error) {
      setError(testError.message);
    } else {
      setError('Failed creating test print');
    }
  } finally {
    setIsSendingPrinterTest(false);
  }
}, [currentEmployee?.id, employeeSession, isManager, selectedTestPrinterId, tenantId]);

// Summary: Updates print-job status from manager diagnostics.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleUpdatePrintJobStatus = useCallback(async (
  jobId: number,
  status: 'queued' | 'claimed' | 'printed' | 'failed',
) => {
  if (!employeeSession || !isManager) {
    return;
  }

  setIsUpdatingPrintJob(true);
  setError(null);
  try {
    const updated = await printersService.updatePrintJobStatus(tenantId, jobId, status, employeeSession);
    setPrintJobs((previous) => previous.map((job) => (job.id === updated.id ? updated : job)));
  } catch (statusError: unknown) {
    if (statusError instanceof Error) {
      setError(statusError.message);
    } else {
      setError('Failed updating print job status');
    }
  } finally {
    setIsUpdatingPrintJob(false);
  }
}, [employeeSession, isManager, tenantId]);

// Summary: Updates employee settings form state.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const onChangeForm = (key: keyof EmployeeFormState, value: string) => {
  setForm((previous) => ({
    ...previous,
    [key]: value,
  }));
};

// Summary: Enters employee edit mode and captures diff baseline.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const startEditEmployee = (employee: EmployeeView) => {
  setEditingEmployeeId(employee.id);
  setPhotoFile(null);
  setForm(buildEmployeeEditForm(employee));
  setEditingEmployeeBaseline(buildEmployeeBaseline(employee));
};

// Summary: Resets employee form back to create mode.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const resetForm = () => {
  setEditingEmployeeId(null);
  setEditingEmployeeBaseline(null);
  setPhotoFile(null);
  setForm(INITIAL_FORM);
};

// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const onChangeProductFormRow = <K extends keyof ProductFormState>(index: number, key: K, value: ProductFormState[K]) => {
  setProductFormRows((previous) => updateProductFormRowField(previous, index, key, value));
};

const onAddProductFormRow = () => {
  setProductFormRows((previous) => addProductFormRow(previous));
};

const onRemoveProductFormRow = (index: number) => {
  setProductFormRows((previous) => removeProductFormRow(previous, index));
};

// Summary: Enters product edit mode and captures diff baseline. Variants/combos are now a
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const startEditProduct = (product: ProductView) => {
  const assignedAreaPrintRuleId = findAreaPrintRuleIdForProduct(areaPrintRules, product.id);
  setEditingProductId(product.id);
  setProductFormRows([{
    ...createProductFormRow(),
    ...buildProductEditForm(product),
    areaPrintEnabled: assignedAreaPrintRuleId !== null,
    areaPrintRuleId: assignedAreaPrintRuleId,
  }]);
  setEditingProductBaseline(buildProductBaseline(product));
  setEditingProductAreaPrintRuleId(assignedAreaPrintRuleId);
};

// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const resetProductForm = () => {
  setEditingProductId(null);
  setProductFormRows([createProductFormRow()]);
  setEditingProductBaseline(null);
  setEditingProductAreaPrintRuleId(null);
};

// Create mode submits every row sequentially, stopping on the first failure (no rollback).
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleSaveProduct = async (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();

  if (!employeeSession || !isManager || saveProductInFlightRef.current) {
    return;
  }

  if (editingProductId !== null) {
    const row = productFormRows[0];
    const normalizedName = row.name.trim();
    const parsedPrice = Number(row.price);
    const targetAreaPrintRuleId = row.areaPrintEnabled ? (row.areaPrintRuleId ?? null) : null;

    if (!normalizedName) {
      setError('Product name is required');
      return;
    }

    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setError('Product price must be a valid number >= 0');
      return;
    }

    if (row.areaPrintEnabled && !targetAreaPrintRuleId) {
      setError('יש לבחור הגדרת הדפסה אזורית');
      return;
    }

    saveProductInFlightRef.current = true;
    setIsSavingProduct(true);
    setError(null);

    try {
      // Summary: Build a partial PATCH body holding only fields whose value changed vs. the baseline captured at startEditProduct. If no field changed and no new image was selected, skip the API call entirely.
      // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
      const updatePayload = buildProductUpdatePayload(
        {
          name: normalizedName,
          price: roundMoney(parsedPrice),
          printerIds: row.printerIds,
          kds: row.kds,
          categoryId: row.categoryId,
        },
        editingProductBaseline,
      );
      const hasFieldChanges = Object.keys(updatePayload).length > 0;
      const hasAreaPrintChange = targetAreaPrintRuleId !== editingProductAreaPrintRuleId;
      if (!hasFieldChanges && !row.imageFile && !hasAreaPrintChange) {
        resetProductForm();
        return;
      }

      if (hasFieldChanges) {
        await productsService.update(editingProductId, updatePayload, employeeSession);
        setSettingsProducts((previous) =>
          previous.map((product) =>
            product.id === editingProductId
              ? {
                ...product,
                ...(updatePayload.name !== undefined ? { name: updatePayload.name } : {}),
                ...(updatePayload.price !== undefined ? { price: updatePayload.price } : {}),
                ...(updatePayload.printerIds !== undefined ? { printerIds: updatePayload.printerIds } : {}),
                ...(updatePayload.kds !== undefined ? { kds: updatePayload.kds } : {}),
                ...(updatePayload.categoryId !== undefined ? { categoryId: updatePayload.categoryId } : {}),
              }
              : product,
          ),
        );
      }

      if (row.imageFile) {
        await productsService.uploadImage(editingProductId, row.imageFile, employeeSession);
      }

      if (hasAreaPrintChange) {
        const nextAreaRulesPatch = applyAreaPrintRuleToProduct(areaPrintRules, editingProductId, targetAreaPrintRuleId);
        if (nextAreaRulesPatch.changed) {
          await sessionsService.updateAreaPrintRules(tenantId, nextAreaRulesPatch.rules, employeeSession);
          setAreaPrintRules(nextAreaRulesPatch.rules);
        }
      }

      resetProductForm();
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Failed saving product');
    } finally {
      saveProductInFlightRef.current = false;
      setIsSavingProduct(false);
    }
    return;
  }

  const validationError = validateProductFormRows(productFormRows);
  if (validationError) {
    setError(validationError.message);
    return;
  }

  saveProductInFlightRef.current = true;
  setIsSavingProduct(true);
  setError(null);

  try {
    let nextAreaRules = areaPrintRules;
    let areaRulesChanged = false;
    for (const row of productFormRows) {
      if (row.areaPrintEnabled && !row.areaPrintRuleId) {
        throw new Error('יש לבחור הגדרת הדפסה אזורית');
      }
      const created = await productsService.create(tenantId, buildProductCreatePayload(row), employeeSession);
      if (row.imageFile) {
        await productsService.uploadImage(created.id, row.imageFile, employeeSession);
      }
      const targetAreaPrintRuleId = row.areaPrintEnabled ? (row.areaPrintRuleId ?? null) : null;
      const nextAreaRulesPatch = applyAreaPrintRuleToProduct(nextAreaRules, created.id, targetAreaPrintRuleId);
      if (nextAreaRulesPatch.changed) {
        areaRulesChanged = true;
        nextAreaRules = nextAreaRulesPatch.rules;
      }
    }
    if (areaRulesChanged) {
      await sessionsService.updateAreaPrintRules(tenantId, nextAreaRules, employeeSession);
      setAreaPrintRules(nextAreaRules);
    }
    resetProductForm();
  } catch (saveError: unknown) {
    setError(saveError instanceof Error ? saveError.message : 'Failed saving product');
  } finally {
    saveProductInFlightRef.current = false;
    setIsSavingProduct(false);
  }
};

// Summary: Deactivates a product from manager settings.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleDeactivateProduct = useCallback(async (productId: number) => {
  if (!employeeSession || !isManager) {
    return;
  }

  setIsTogglingProductStatus(true);
  setError(null);

  try {
    await productsService.deactivate(productId, employeeSession);
    setSettingsProducts((previous) =>
      previous.map((product) =>
        product.id === productId ? { ...product, isActive: false } : product,
      ),
    );
  } catch (statusError: unknown) {
    if (statusError instanceof Error) {
      setError(statusError.message);
    } else {
      setError('Failed deactivating product');
    }
  } finally {
    setIsTogglingProductStatus(false);
  }
}, [employeeSession, isManager]);

// Summary: Reactivates a product from manager settings.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleActivateProduct = useCallback(async (productId: number) => {
  if (!employeeSession || !isManager) {
    return;
  }

  setIsTogglingProductStatus(true);
  setError(null);

  try {
    await productsService.activate(productId, employeeSession);
    setSettingsProducts((previous) =>
      previous.map((product) =>
        product.id === productId ? { ...product, isActive: true } : product,
      ),
    );
  } catch (statusError: unknown) {
    if (statusError instanceof Error) {
      setError(statusError.message);
    } else {
      setError('Failed activating product');
    }
  } finally {
    setIsTogglingProductStatus(false);
  }
}, [employeeSession, isManager]);

// Summary: Toggles product KDS flag with optimistic UI and inline rollback on error.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleToggleProductKds = useCallback(async (productId: number, nextKds: boolean) => {
  if (!employeeSession || !isManager || updatingProductKdsIdsRef.current.has(productId)) {
    return;
  }

  updatingProductKdsIdsRef.current.add(productId);
  setUpdatingProductKdsIds((previous) =>
    previous.includes(productId) ? previous : [...previous, productId],
  );
  setError(null);
  setSettingsProducts((previous) =>
    previous.map((product) =>
      product.id === productId ? { ...product, kds: nextKds } : product,
    ),
  );

  try {
    await productsService.update(productId, { kds: nextKds }, employeeSession);
  } catch (toggleError: unknown) {
    setSettingsProducts((previous) =>
      previous.map((product) =>
        product.id === productId ? { ...product, kds: !nextKds } : product,
      ),
    );
    if (toggleError instanceof Error) {
      setError(toggleError.message);
    } else {
      setError('Failed updating KDS flag');
    }
  } finally {
    updatingProductKdsIdsRef.current.delete(productId);
    setUpdatingProductKdsIds((previous) => previous.filter((id) => id !== productId));
  }
}, [employeeSession, isManager]);

const isProductKdsUpdating = useCallback(
  (productId: number) => updatingProductKdsIds.includes(productId),
  [updatingProductKdsIds],
);

// Summary: Inline-creates a category; patches lists from the response so UI never waits on SSE.
const handleCreateCategoryInline = useCallback(
  async (name: string, reportGroup: CategoryReportGroup | null): Promise<number | null> => {
    if (!employeeSession || !isManager || createCategoryInFlightRef.current) {
      return null;
    }
    createCategoryInFlightRef.current = true;
    try {
      const created = await categoriesService.create(tenantId, { name, reportGroup }, employeeSession);
      const now = new Date().toISOString();
      const createdView: CategoryView = {
        id: created.id,
        tenantId,
        name: name.trim(),
        sortOrder: 0,
        reportGroup,
        happyHour: false,
        separateKitchenBon: false,
        parentCategoryPk: null,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };
      const mergeCreated = (previous: CategoryView[]): CategoryView[] =>
        previous.some((category) => category.id === createdView.id)
          ? previous
          : [...previous, createdView];
      setSettingsCategories(mergeCreated);
      setPosCategories(mergeCreated);
      return created.id;
    } catch (createError: unknown) {
      setError(createError instanceof Error ? createError.message : 'Failed creating category');
      return null;
    } finally {
      createCategoryInFlightRef.current = false;
    }
  },
  [employeeSession, isManager, setPosCategories, tenantId],
);

// Summary: Bulk-applies a field subset; optimistic patch of settingsProducts, SSE reconciles.
const handleBulkUpdateProducts = useCallback(
  async (payload: BulkUpdateProductsRequest): Promise<void> => {
    if (!employeeSession || !isManager) {
      return;
    }
    const { ids, ...fields } = payload;
    await productsService.bulkUpdate(payload, employeeSession);
    const idSet = new Set(ids);
    setSettingsProducts((previous) =>
      previous.map((product) =>
        idSet.has(product.id)
          ? {
            ...product,
            ...(fields.categoryId !== undefined ? { categoryId: fields.categoryId } : {}),
            ...(fields.price !== undefined ? { price: fields.price } : {}),
            ...(fields.printerIds !== undefined ? { printerIds: fields.printerIds } : {}),
            ...(fields.kds !== undefined ? { kds: fields.kds } : {}),
          }
          : product,
      ),
    );
  },
  [employeeSession, isManager],
);

// Summary: Resets manager table form back to create mode and clears stale edit references after save/cancel operations.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const resetTableForm = useCallback(() => {
  setEditingTableId(null);
  setSelectedTableIds([]);
  setTableForm(INITIAL_TABLE_FORM);
}, []);

// Summary: Enters table edit mode with current geometry values.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const startEditTable = (table: AreaTableView) => {
  setEditingTableId(table.id);
  setSelectedTableIds([table.id]);
  setTableForm(buildTableEditForm(table));
};

const handleSelectEditTable = useCallback((tableId: number, additive: boolean) => {
  setSelectedTableIds((previous) => {
    if (!additive) {
      return [tableId];
    }
    if (previous.includes(tableId)) {
      return previous.filter((id) => id !== tableId);
    }
    return [...previous, tableId];
  });
}, []);

const handleSelectEditTables = useCallback((tableIds: number[], additive: boolean) => {
  setSelectedTableIds((previous) => {
    if (!additive) {
      return tableIds;
    }
    const union = new Set([...previous, ...tableIds]);
    return [...union];
  });
}, []);

// Summary: Table-mutation behavior (diff-only PATCH path + inline
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const { persistTableUpdate, handleRenameTable, handleChangeShape, handleDuplicateTable, handleDeleteTable, handleCreateAreaByName, handleRenameAreaByName, handleUpdateAreaFloorDecor, handleReorderAreas, handleAreaActiveToggle, handleAddTable, handleResizeSelectedTablesFromMaster, handleAlignSelectedTablesRow, handleAlignSelectedTablesColumn } = useAreaTableEditing({
  employeeSession,
  isManager,
  tenantId,
  areaMode,
  selectedAreaId,
  editingTableId,
  selectedTableIds,
  copiedTables,
  settingsAreasRef,
  tableMutationInFlightRef,
  setSettingsAreas,
  setActiveAreas,
  setSelectedAreaId,
  setSelectedTableIds,
  setEditingTableId,
  setTableForm,
  setIsSavingTable,
  setCopiedTables,
  setError,
  resetTableForm,
});


// Summary: Exits area edit mode and returns to floor view.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleExitEditMode = useCallback(() => {
  setAreaMode('view');
  setIsSizeMasterPickMode(false);
  resetTableForm();
}, [setAreaMode, resetTableForm]);

useEffect(() => {
  setSelectedTableIds([]);
  setIsSizeMasterPickMode(false);
}, [selectedAreaId, areaMode]);

useEffect(() => {
  if (selectedTableIds.length < 2 && isSizeMasterPickMode) {
    setIsSizeMasterPickMode(false);
  }
}, [selectedTableIds, isSizeMasterPickMode]);

const { beginCanvasInteraction } = useTableCanvasInteraction({
  areaMode,
  isManager,
  selectedTableIds,
  setActiveAreas,
  setSettingsAreas,
  settingsAreasRef,
  setEditingTableId,
  setTableForm,
  setError,
  interactionRef,
  isPersistingInteractionRef,
  onAlignmentGuidesChange: setAlignmentGuides,
  onAlignmentGapHintsChange: setAlignmentGapHints,
  persistTableUpdate,
});

const {
  viewArea,
  editArea,
  selectedAreaList,
  viewAreaCanvasBounds,
  editAreaCanvasBounds,
  viewAreaCanvasScale,
} = useAreaCanvasPresentation({
  activeAreas,
  settingsAreas,
  selectedAreaId,
  areaMode,
  viewCanvasHostWidth,
  viewCanvasHostHeight,
  editCanvasHostWidth,
  editCanvasHostHeight,
  viewportWidth,
});

// Summary: Validates selected employee photo client-side before upload to reduce invalid requests and keep manager settings UX predictable. Changes here affect upload pre-validation behavior in POS settings.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handlePhotoFileChange = (candidate: File | null) => {
  if (!candidate) {
    setPhotoFile(null);
    return;
  }

  const allowedMimeTypes = new Set(['image/jpeg', 'image/webp']);
  if (!allowedMimeTypes.has(candidate.type)) {
    setError('Employee photo must be JPEG or WebP');
    setPhotoFile(null);
    return;
  }

  if (candidate.size > 3 * 1024 * 1024) {
    setError('Employee photo must be 3MB or smaller');
    setPhotoFile(null);
    return;
  }

  setError(null);
  setPhotoFile(candidate);
};

// Summary: Validates selected product image client-side before upload.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const onProductFormRowImageChange = (index: number, candidate: File | null) => {
  if (!candidate) {
    setProductFormRows((previous) => updateProductFormRowImage(previous, index, null));
    return;
  }

  const allowedMimeTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  if (!allowedMimeTypes.has(candidate.type)) {
    setError('Product image must be JPEG, PNG, or WebP');
    setProductFormRows((previous) => updateProductFormRowImage(previous, index, null));
    return;
  }

  if (candidate.size > 3 * 1024 * 1024) {
    setError('Product image must be 3MB or smaller');
    setProductFormRows((previous) => updateProductFormRowImage(previous, index, null));
    return;
  }

  setError(null);
  setProductFormRows((previous) => updateProductFormRowImage(previous, index, candidate));
};

// Summary: Handles manager create/update employee actions from settings form while preserving list consistency. Changes here affect employee lifecycle data mutation behavior in POS settings.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const handleSaveEmployee = async (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  if (!employeeSession || !isManager) {
    return;
  }

  setIsSavingEmployee(true);
  setError(null);

  try {
    const allowSalaryEdit = attendanceUiEnabled;
    const trimmedSalary = allowSalaryEdit ? form.baseHourlySalary.trim() : '';
    let parsedSalary: number | null = null;
    if (trimmedSalary.length > 0) {
      const salaryCandidate = Number(trimmedSalary);
      if (!Number.isFinite(salaryCandidate) || salaryCandidate < 0) {
        setError('שכר שעתי חייב להיות מספר חיובי או ריק');
        return;
      }
      parsedSalary = salaryCandidate;
    }

    if (editingEmployeeId !== null) {
      // Summary: Build a partial PATCH body holding only fields whose value changed vs. the baseline captured at startEditEmployee. PIN is always conditional (only set when manager typed a new one). If nothing changed AND no new PIN, skip the API call entirely. The photo upload remains an independent call (separate endpoint, separate decision: only fires when a new file was selected). Backend (EmployeesService.updateTenantEmployee) merges missing fields with the existing row.
      // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
      const baseline = editingEmployeeBaseline;
      const trimmedFullName = form.fullName.trim();
      const trimmedPin = form.pin.trim();
      const payload: UpdateEmployeeRequest = {};
      if (baseline === null || trimmedFullName !== baseline.fullName) {
        payload.fullName = trimmedFullName;
      }
      if (baseline === null || form.role !== baseline.role) {
        payload.role = form.role;
      }
      if (allowSalaryEdit && (baseline === null || parsedSalary !== baseline.baseHourlySalary)) {
        payload.baseHourlySalary = parsedSalary;
      }
      if (trimmedPin) {
        payload.pin = trimmedPin;
      }
      const hasFieldChanges = Object.keys(payload).length > 0;
      if (hasFieldChanges) {
        await employeesService.update(editingEmployeeId, payload, employeeSession);
        setSettingsEmployees((previous) =>
          previous.map((item) =>
            item.id === editingEmployeeId
              ? {
                ...item,
                ...(payload.fullName !== undefined ? { fullName: payload.fullName } : {}),
                ...(payload.role !== undefined ? { role: payload.role } : {}),
                ...(payload.baseHourlySalary !== undefined ? { baseHourlySalary: payload.baseHourlySalary } : {}),
              }
              : item,
          ),
        );
      }
      if (photoFile) {
        await employeesService.uploadPhoto(editingEmployeeId, photoFile, employeeSession);
      }
    } else {
      const payload: CreateEmployeeRequest = {
        fullName: form.fullName.trim(),
        pin: form.pin.trim(),
        role: form.role,
        baseHourlySalary: allowSalaryEdit ? parsedSalary : null,
      };

      const created = await employeesService.create(tenantId, payload, employeeSession);
      if (photoFile) {
        await employeesService.uploadPhoto(created.id, photoFile, employeeSession);
      }
    }

    resetForm();
  } catch (saveError: unknown) {
    if (saveError instanceof Error) {
      setError(saveError.message);
    } else {
      setError('Failed saving employee');
    }
  } finally {
    setIsSavingEmployee(false);
  }
};

// Summary: Performs manager-triggered employee deactivation from settings and updates local list state for immediate status feedback. Changes here affect employee lockout UX and active-list refresh behavior.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const deactivateEmployee = useCallback(async (employee: EmployeeView) => {
  if (!employeeSession || !isManager) {
    return;
  }

  if (!window.confirm(`Deactivate ${employee.fullName}?`)) {
    return;
  }

  setError(null);

  try {
    await employeesService.deactivate(employee.id, employeeSession);
    setSettingsEmployees((previous) =>
      previous.map((item) => (item.id === employee.id ? { ...item, isActive: false } : item)),
    );
    setAvailableEmployees((previous) => previous.filter((item) => item.id !== employee.id));
  } catch (deactivateError: unknown) {
    if (deactivateError instanceof Error) {
      setError(deactivateError.message);
    } else {
      setError('Failed deactivating employee');
    }
  }
}, [employeeSession, isManager]);

// Summary: Performs manager-triggered employee reactivation from settings and updates local list state for immediate status feedback. Changes here affect employee access restoration UX.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const activateEmployee = useCallback(async (employee: EmployeeView) => {
  if (!employeeSession || !isManager) {
    return;
  }

  if (!window.confirm(`Activate ${employee.fullName}?`)) {
    return;
  }

  setError(null);

  try {
    await employeesService.activate(employee.id, employeeSession);
    setSettingsEmployees((previous) =>
      previous.map((item) => (item.id === employee.id ? { ...item, isActive: true } : item)),
    );
  } catch (activateError: unknown) {
    if (activateError instanceof Error) {
      setError(activateError.message);
    } else {
      setError('Failed activating employee');
    }
  }
}, [employeeSession, isManager]);

const visibleTabs = useMemo(() => {
  const baseTabs: Array<{ key: PosTabKey; label: string }> = [
    { key: 'areas', label: 'אזורים' },
    { key: 'orders', label: 'הזמנות' },
    { key: 'limit-items', label: 'הגבלת פריטים' },
  ];
  return isKioskMode ? baseTabs.filter((tab) => tab.key !== 'areas') : baseTabs;
}, [isKioskMode]);

const selectedTableOrder = selectedOrder;

// The employee SSE proves the device is live; this heartbeat adds the table currently being
// worked on. It expires server-side after 70 seconds if the device closes or disconnects.
useEffect(() => {
  if (!employeeSession || activeTab !== 'orders' || selectedOrderTableId === null) {
    return;
  }

  let disposed = false;
  const touch = () => {
    void tablePresenceService.touch(tenantId, selectedOrderTableId, employeeSession).catch(() => {
      // The authenticated client handles invalid/replaced sessions globally. A transient
      // heartbeat failure must not interrupt the active POS screen.
    });
  };
  touch();
  const heartbeatId = window.setInterval(touch, 10_000);
  const handlePageHide = () => {
    tablePresenceService.removeOnPageExit(tenantId, selectedOrderTableId, employeeSession);
  };
  window.addEventListener('pagehide', handlePageHide);

  return () => {
    window.clearInterval(heartbeatId);
    window.removeEventListener('pagehide', handlePageHide);
    if (!disposed) {
      disposed = true;
      void tablePresenceService.remove(tenantId, selectedOrderTableId, employeeSession).catch(() => undefined);
    }
  };
}, [activeTab, employeeSession, selectedOrderTableId, tenantId]);

const ensureTableIsNotInUse = useCallback(async (tableId: number): Promise<boolean> => {
  if (!employeeSession) {
    return false;
  }
  const presence = await tablePresenceService.check(tenantId, tableId, employeeSession);
  if (!presence.occupied) {
    return true;
  }
  notifications.show({
    color: 'orange',
    title: 'השולחן בשימוש',
    message: presence.employeeName
      ? `${presence.employeeName} עובד/ת כרגע בשולחן. לא ניתן לבטל, למזג, לפצל או להעביר אותו.`
      : 'לא ניתן לבטל, למזג, לפצל או להעביר שולחן בזמן שמשתמש אחר עובד בו.',
    autoClose: 3000,
  });
  return false;
}, [employeeSession, tenantId]);

const ensureTablesAreNotInUse = useCallback(async (tableIds: number[]): Promise<boolean> => {
  const uniqueTableIds = [...new Set(tableIds)];
  // Check all tables concurrently (each is an independent HTTP round-trip) instead of one at a
  // time — merging N tables no longer pays N sequential round-trips just for this pre-check.
  // ensureTableIsNotInUse shows its own toast on failure; only surface the first one so multiple
  // occupied tables don't stack duplicate notifications.
  const results = await Promise.all(uniqueTableIds.map((tableId) => ensureTableIsNotInUse(tableId)));
  return results.every(Boolean);
}, [ensureTableIsNotInUse]);

const ensureOrdersAreNotProcessingCredit = useCallback(async (orderIds: Array<number | null | undefined>): Promise<boolean> => {
  if (!employeeSession) {
    return false;
  }
  const uniqueOrderIds = [...new Set(orderIds.filter((orderId): orderId is number => orderId != null))];
  try {
    // Same concurrency fix as ensureTablesAreNotInUse — check every order's credit status at
    // once rather than serially.
    const results = await Promise.all(
      uniqueOrderIds.map((orderId) => paymentsService.listForOrder(tenantId, orderId, employeeSession)),
    );
    const hasPendingCredit = results.some((lines) => lines.some((line) => line.hasPendingCreditCharge));
    if (hasPendingCredit) {
      notifications.show({
        color: 'orange',
        title: 'תשלום אשראי בעיבוד',
        message: 'לא ניתן לבטל, להעביר או למזג שולחן עד לסיום התשלום.',
        autoClose: 3000,
      });
      return false;
    }
    return true;
  } catch {
    notifications.show({
      color: 'red',
      title: 'לא ניתן לבצע פעולה',
      message: 'לא ניתן לבדוק את מצב התשלום כרגע. נסו שוב בעוד רגע.',
      autoClose: 3000,
    });
    return false;
  }
}, [employeeSession, tenantId]);

const receiptTableOrder = useMemo(() => {
  if (!isReceiptScreenOpen) return null;
  return selectedTableOrder;
}, [isReceiptScreenOpen, selectedTableOrder]);

const guestNameDialogOrder = useMemo(() => {
  if (selectedTableOrder) {
    return selectedTableOrder;
  }
  if (selectedOrderId !== null) {
    return activeOrders.find((entry) => entry.id === selectedOrderId) ?? null;
  }
  if (selectedOrderTableId !== null) {
    return activeOrders.find((entry) => entry.tableId === selectedOrderTableId) ?? null;
  }
  return null;
}, [activeOrders, selectedOrderId, selectedOrderTableId, selectedTableOrder]);

// Fetches a row's bill lines/payments on demand and opens its preview (no order selection).
const handleOpenBillPreview = useCallback(async (orderId: number) => {
  if (billPreviewInFlightRef.current || !employeeSession) return;
  const order = activeOrders.find((candidate) => candidate.id === orderId);
  if (!order) return;
  billPreviewInFlightRef.current = true;
  try {
    const [lines, payments] = await Promise.all([
      orderlinesService.listForOrder(tenantId, orderId, employeeSession),
      paymentsService.listForOrder(tenantId, orderId, employeeSession),
    ]);
    setBillPreview({ order, orderlines: lines, paymentlines: payments });
  } catch {
    notifications.show({ color: 'red', title: 'שגיאה', message: 'שגיאה בטעינת החשבון', autoClose: 3000 });
  } finally {
    billPreviewInFlightRef.current = false;
  }
}, [activeOrders, employeeSession, tenantId]);

const handleUpdateDinersClick = useCallback((dinersCount: number) => {
  const order = selectedTableOrder;
  if (order) void handleUpdateDiners(order.id, dinersCount);
}, [selectedTableOrder, handleUpdateDiners]);

// Summary: Resolves the operator's avatar URL by matching the active session employee against the loaded employee list (EmployeeIdentity itself does not carry a photoUrl).
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-posshellpage
const currentEmployeePhotoUrl = useMemo(() => {
  const match = availableEmployees.find((employee) => employee.id === currentEmployee?.id);
  return match?.photoUrl ?? null;
}, [availableEmployees, currentEmployee?.id]);

const handleAreasTableClick = useCallback((tableId: number, tableLabel: string) => {
  void handleViewTableClick(tableId, tableLabel);
}, [handleViewTableClick]);

const handleOpenTableClick = useCallback(() => {
  setIsDinersNumPadOpen(true);
}, []);

const shouldPromptGuestNameOnEntry = customerNameOnOrderEntry && viewportWidth >= 768;

const handleDinersConfirm = useCallback(async (dinersCount: number | null) => {
  if (dinersCount === null || dinersCount < 1 || dinersCount > 99) {
    return;
  }
  const opened = await handleOpenOrderWithDiners(dinersCount);
  if (!opened) {
    return;
  }
  setIsDinersNumPadOpen(false);
  if (pendingMergeAfterDinersRef.current) {
    pendingMergeAfterDinersRef.current = false;
    void handleConfirmMerge();
  } else {
    setActiveTab('orders');
    if (shouldPromptGuestNameOnEntry) {
      setIsGuestNameEditOpen(true);
    }
  }
}, [handleOpenOrderWithDiners, handleConfirmMerge, shouldPromptGuestNameOnEntry]);

// Summary: Opens a tableless take-away order from the areas banner; jumps to orders screen.
const handleOpenTakeAwayClick = useCallback(async () => {
  if (!employeeSession) return;
  if (!activeSession) {
    openSessionFlow.requestOpenSession({ orderVariant: 'take_away' });
    return;
  }
  if (stationWaiter.intercept({ kind: 'take_away' })) return;
  const opened = await handleOpenTakeAway();
  if (opened) {
    setActiveTab('orders');
    if (shouldPromptGuestNameOnEntry) {
      setIsGuestNameEditOpen(true);
    }
  }
}, [activeSession, employeeSession, handleOpenTakeAway, openSessionFlow, shouldPromptGuestNameOnEntry, stationWaiter.intercept]);
const handleOpenKioskDineIn = useCallback(async () => {
  if (!employeeSession) return;
  if (!activeSession) {
    openSessionFlow.requestOpenSession({ orderVariant: 'kiosk_dine_in' });
    return;
  }
  if (stationWaiter.intercept({ kind: 'kiosk_dine_in' })) return;
  const opened = await handleOpenTakeAway('kiosk_dine_in');
  if (opened) {
    setActiveTab('orders');
    if (shouldPromptGuestNameOnEntry) {
      setIsGuestNameEditOpen(true);
    }
  }
}, [activeSession, employeeSession, handleOpenTakeAway, openSessionFlow, shouldPromptGuestNameOnEntry, stationWaiter.intercept]);

const [pendingQuickChargePayment, setPendingQuickChargePayment] = useState<QuickChargePreparedPayment | null>(null);
const [itemActionsOrderId, setItemActionsOrderId] = useState<number | null>(null);

const quickCharge = useQuickCharge({
  openQuickChargeOrder: () => handleOpenTakeAway('quick_charge'),
  applyAmount: applyQuickChargeAmount,
  cancelOrder: cancelQuickCharge,
  clearAmountPrompt: clearQuickChargeAmountPrompt,
  onPrepared: setPendingQuickChargePayment,
  onError: (message) => notifications.show({ color: 'red', title: 'שגיאה', message, autoClose: 3000 }),
});

useEffect(() => {
  const prepared = pendingQuickChargePayment;
  if (!prepared || !paymentlines.some((line) => line.id === prepared.paymentlineId && line.status === 'new')) {
    return;
  }
  setPendingQuickChargePayment(null);
  setActiveTab(landingTabRef.current);
  if (prepared.tender.kind === 'cash') {
    void handlePayAndCloseIfDone(prepared.orderId, prepared.paymentlineId);
  } else if (prepared.tender.kind === 'pinpad') {
    void handleChargeCredit(
      prepared.orderId,
      prepared.paymentlineId,
      prepared.tender.terminalId,
      prepared.tender.pinpadId,
    );
  } else if (prepared.tender.kind === 'manual') {
    handleOpenManualCredit(
      prepared.orderId,
      prepared.paymentlineId,
      prepared.tender.terminalId,
      prepared.tender.pinpadId,
    );
  } else {
    handleOpenTapOnPhone(prepared.orderId, prepared.paymentlineId);
  }
}, [
  handleChargeCredit,
  handleOpenManualCredit,
  handleOpenTapOnPhone,
  handlePayAndCloseIfDone,
  paymentlines,
  pendingQuickChargePayment,
]);

const handleTransferLongPress = useCallback(async (tableId: number, tableLabel: string, orderId: number) => {
  if (!employeeSession || isLoadingTransferableOrderlines) return;
  if (!await ensureTableIsNotInUse(tableId)) {
    return;
  }
  if (!await ensureOrdersAreNotProcessingCredit([orderId])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(orderId)) {
    showPendingApprovalBlockingToast(orderId);
    return;
  }
  setIsLoadingTransferableOrderlines(true);
  try {
    const result = await transferService.listTransferableOrderlines(tenantId, orderId, employeeSession);
    setTransferableOrderlines(result.orderlines);
    setTransferRemainingToPay(result.remainingToPay);
    if (result.orderlines.length === 0) {
      notifications.show({ color: 'blue', title: 'אין פריטים', message: 'אין פריטים להעברה בשולחן זה', autoClose: 3000 });
      return;
    }
    handleBeginTransfer(orderId, tableId, tableLabel);
  } catch (err: unknown) {
    if (err instanceof Error) {
      notifications.show({ color: 'red', title: 'שגיאה', message: err.message, autoClose: 3000 });
    }
  } finally {
    setIsLoadingTransferableOrderlines(false);
  }
}, [employeeSession, tenantId, handleBeginTransfer, isLoadingTransferableOrderlines, ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTableIsNotInUse, showPendingApprovalBlockingToast]);

const handleMergeLongPress = useCallback(async (tableId: number, tableLabel: string, orderId: number | null) => {
  if (!await ensureTableIsNotInUse(tableId)) {
    return;
  }
  if (!await ensureOrdersAreNotProcessingCredit([orderId])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(orderId)) {
    if (orderId != null) showPendingApprovalBlockingToast(orderId);
    return;
  }
  // Non-managers can't clear a persistent discount/tip conflict — block before entering merge.
  if (!isManager) {
    const initiatingOrder = activeOrders.find((order) => order.tableId === tableId) ?? null;
    if (hasPersistentMergeConflict(initiatingOrder)) {
      notifications.show(PERSISTENT_MERGE_CONFLICT_TOAST);
      return;
    }
  }
  handleBeginMerge(tableId, tableLabel, orderId);
}, [activeOrders, ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTableIsNotInUse, handleBeginMerge, isManager, showPendingApprovalBlockingToast]);

const handleConfirmMergeWithTablePresence = useCallback(async () => {
  if (mergeMode.step !== 'pick-tables' || isTransferring) {
    return;
  }
  // Show the loading/disabled state immediately on click, before the pre-flight presence/credit
  // checks run — those are real network round-trips (parallelized below, but each still pays
  // full latency), and without this the button looks unresponsive for their whole duration on a
  // slow connection. handleConfirmMerge's own finally clears this on the success/merge path; the
  // early-return paths here (pre-flight failures) must clear it themselves since they never reach
  // handleConfirmMerge.
  setIsTransferring(true);
  let preflightPassed = false;
  try {
    if (!await ensureTablesAreNotInUse(mergeMode.selectedTableIds)) {
      return;
    }
    const orderIds = mergeMode.selectedTableIds.map((tableId) =>
      activeOrders.find((order) => order.tableId === tableId)?.id,
    );
    if (!await ensureOrdersAreNotProcessingCredit(orderIds)) {
      return;
    }
    preflightPassed = true;
  } finally {
    if (!preflightPassed) {
      setIsTransferring(false);
    }
  }
  await handleConfirmMerge();
}, [activeOrders, ensureOrdersAreNotProcessingCredit, ensureTablesAreNotInUse, handleConfirmMerge, mergeMode, setIsTransferring]);

const handleToggleMergeTableWithApprovalGuard = useCallback(async (tableId: number, tableLabel: string) => {
  if (!await ensureTableIsNotInUse(tableId)) {
    return;
  }
  const tableOrder = activeOrders.find((order) => order.tableId === tableId) ?? null;
  if (!await ensureOrdersAreNotProcessingCredit([tableOrder?.id])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(tableOrder?.id)) {
    if (tableOrder?.id != null) showPendingApprovalBlockingToast(tableOrder.id);
    return;
  }
  // Rule 47: the conflict can come from any involved order — deny selecting a conflicting table.
  if (!isManager && hasPersistentMergeConflict(tableOrder)) {
    notifications.show(PERSISTENT_MERGE_CONFLICT_TOAST);
    return;
  }
  handleToggleMergeTable(tableId, tableLabel);
}, [activeOrders, ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTableIsNotInUse, handleToggleMergeTable, isManager, showPendingApprovalBlockingToast]);

const handleBeginSplitMergeWithApprovalGuard = useCallback(async (tableId: number) => {
  const mergeLink = activeMergeLinks.find((link) => link.tablePk === tableId) ?? null;
  const groupTableIds = activeMergeLinks
    .filter((link) => link.orderPk === mergeLink?.orderPk)
    .map((link) => link.tablePk);
  if (!await ensureTablesAreNotInUse(groupTableIds)) {
    return;
  }
  if (!await ensureOrdersAreNotProcessingCredit([mergeLink?.orderPk])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(mergeLink?.orderPk)) {
    if (mergeLink?.orderPk != null) showPendingApprovalBlockingToast(mergeLink.orderPk);
    return;
  }
  handleBeginSplitMerge(tableId);
}, [activeMergeLinks, ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTablesAreNotInUse, handleBeginSplitMerge, showPendingApprovalBlockingToast]);

const handleConfirmSplitMergeWithTablePresence = useCallback(async (linkIds: number[]) => {
  if (splitMergeOrderPk === null || isSplittingMerge) {
    return;
  }
  const groupTableIds = activeMergeLinks
    .filter((link) => link.orderPk === splitMergeOrderPk)
    .map((link) => link.tablePk);
  if (!await ensureTablesAreNotInUse(groupTableIds)) {
    return;
  }
  await handleConfirmSplitMerge(linkIds);
}, [activeMergeLinks, ensureTablesAreNotInUse, handleConfirmSplitMerge, isSplittingMerge, splitMergeOrderPk]);

const handleCancelTableLongPress = useCallback(async (tableId: number, tableLabel: string, orderId: number) => {
  if (!await ensureTableIsNotInUse(tableId)) {
    return;
  }
  if (!await ensureOrdersAreNotProcessingCredit([orderId])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(orderId)) {
    showPendingApprovalBlockingToast(orderId);
    return;
  }
  setCancelTableConfirm({ tableId, tableLabel, orderId });
}, [ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTableIsNotInUse, showPendingApprovalBlockingToast]);

const handleItemActionsLongPress = useCallback(async (orderId: number) => {
  if (await ensureOrderHasPendingApproval(orderId)) {
    showPendingApprovalBlockingToast(orderId);
    return;
  }
  setItemActionsOrderId(orderId);
}, [ensureOrderHasPendingApproval, showPendingApprovalBlockingToast]);

const closeItemActions = useCallback(() => setItemActionsOrderId(null), []);
const itemActionsOrder = useMemo(
  () => activeOrders.find((entry) => entry.id === itemActionsOrderId) ?? null,
  [activeOrders, itemActionsOrderId],
);
useEffect(() => {
  if (itemActionsOrderId === null || itemActionsOrder) return;
  closeItemActions();
  notifications.show({ color: 'blue', title: 'ההזמנה נסגרה', message: 'הפעולה בוטלה', autoClose: 3000 });
}, [closeItemActions, itemActionsOrder, itemActionsOrderId]);

const handleConfirmCancelTable = useCallback(async () => {
  if (!cancelTableConfirm || !employeeSession || cancelTableInFlightRef.current) {
    return;
  }

  if (!await ensureTableIsNotInUse(cancelTableConfirm.tableId)) {
    return;
  }
  if (!await ensureOrdersAreNotProcessingCredit([cancelTableConfirm.orderId])) {
    return;
  }

  cancelTableInFlightRef.current = true;
  setIsCancelingTable(true);
  try {
    const currentOrderId = cancelTableConfirm.orderId;
    const lines = selectedOrderId === currentOrderId
      ? orderlines
      : await orderlinesService.listForOrder(tenantId, currentOrderId, employeeSession);
    const paidLines = selectedOrderId === currentOrderId
      ? paymentlines
      : await paymentsService.listForOrder(tenantId, currentOrderId, employeeSession);

    const items = buildCancelAllOrderlineSelections(lines);

    if (items.length === 0) {
      notifications.show({
        color: 'orange',
        title: 'אין פריטים לביטול',
        message: 'אין פריטים שנשלחו לביטול בשולחן הזה.',
        autoClose: 3000,
      });
      setCancelTableConfirm(null);
      return;
    }

    if (hasPaidPaymentline(paidLines)) {
      notifications.show({
        color: 'red',
        title: 'לא ניתן לבטל הזמנה',
        message: 'קיימת שורת תשלום משולמת',
        autoClose: 3000,
      });
      setCancelTableConfirm(null);
      return;
    }

    setCancelTableApprovalRequest({
      orderId: currentOrderId,
      tableLabel: cancelTableConfirm.tableLabel,
      items,
      orderlines: lines,
      cancelScope: 'order',
    });
    setCancelTableConfirm(null);
  } catch (err: unknown) {
    notifications.show({
      color: 'red',
      title: 'לא ניתן לבטל שולחן',
      message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
      autoClose: 3000,
    });
  } finally {
    setIsCancelingTable(false);
    cancelTableInFlightRef.current = false;
  }
}, [cancelTableConfirm, employeeSession, ensureOrdersAreNotProcessingCredit, ensureTableIsNotInUse, orderlines, paymentlines, selectedOrderId, tenantId]);

const handleSubmitCancelTableApproval = useCallback(async (reason: ApprovalReason, note: string | null) => {
  if (!cancelTableApprovalRequest || !employeeSession || cancelTableApprovalInFlightRef.current) {
    return;
  }

  cancelTableApprovalInFlightRef.current = true;
  setIsSubmittingCancelTableApproval(true);
  try {
    const created = await requestCancelApproval(
      tenantId,
      cancelTableApprovalRequest.orderId,
      cancelTableApprovalRequest.items,
      employeeSession,
      reason,
      note,
      cancelTableApprovalRequest.cancelScope,
    );
    if (!isManager) {
      setPendingApprovalIds((previous) => {
        const next = new Set(previous);
        next.add(created.id);
        return next;
      });
      setPendingApprovalOrderIds((previous) => {
        const next = new Set(previous);
        next.add(created.orderId);
        return next;
      });
    }
    setCancelTableApprovalRequest(null);
    notifications.show({
      color: isManager ? 'green' : 'blue',
      title: isManager ? 'השולחן בוטל' : 'הבקשה נשלחה',
      message: isManager ? `שולחן ${cancelTableApprovalRequest.tableLabel} בוטל.` : 'ממתין לאישור מנהל',
      autoClose: 3000,
    });
  } catch (err: unknown) {
    notifications.show({
      color: 'red',
      title: 'שגיאה בשליחת הבקשה',
      message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
      autoClose: 3000,
    });
  } finally {
    setIsSubmittingCancelTableApproval(false);
    cancelTableApprovalInFlightRef.current = false;
  }
}, [cancelTableApprovalRequest, employeeSession, isManager, tenantId, setPendingApprovalIds, setPendingApprovalOrderIds]);

const handleTransferFromOrdersPanel = useCallback(async () => {
  if (!employeeSession || !selectedOrderTableId || isLoadingTransferableOrderlines) return;
  if (!await ensureTableIsNotInUse(selectedOrderTableId)) {
    return;
  }
  const tableOrder = activeOrders.find((o) => o.tableId === selectedOrderTableId);
  if (!tableOrder) return;
  if (!await ensureOrdersAreNotProcessingCredit([tableOrder.id])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(tableOrder.id)) {
    showPendingApprovalBlockingToast(tableOrder.id);
    return;
  }
  setIsLoadingTransferableOrderlines(true);
  try {
    const result = await transferService.listTransferableOrderlines(tenantId, tableOrder.id, employeeSession);
    setTransferableOrderlines(result.orderlines);
    setTransferRemainingToPay(result.remainingToPay);
    if (result.orderlines.length === 0) {
      notifications.show({ color: 'blue', title: 'אין פריטים', message: 'אין פריטים להעברה', autoClose: 3000 });
      return;
    }
    handleBeginTransfer(tableOrder.id, selectedOrderTableId, selectedOrderTableLabel ?? String(selectedOrderTableId));
    setActiveTab('areas');
  } catch (err: unknown) {
    if (err instanceof Error) {
      notifications.show({ color: 'red', title: 'שגיאה', message: err.message, autoClose: 3000 });
    }
  } finally {
    setIsLoadingTransferableOrderlines(false);
  }
}, [employeeSession, tenantId, selectedOrderTableId, selectedOrderTableLabel, activeOrders, handleBeginTransfer, isLoadingTransferableOrderlines, ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTableIsNotInUse, showPendingApprovalBlockingToast]);

const handlePickTargetTable = useCallback(async (targetTableId: number, targetTableLabel: string) => {
  void targetTableLabel;
  if (transferMode.step !== 'pick-table' || isTransferring) return;
  if (!await ensureTablesAreNotInUse([transferMode.sourceTableId, targetTableId])) {
    return;
  }
  const selectedQtyByLineId = new Map(transferMode.selectedLines.map((line) => [line.orderlineId, line.quantity]));
  const targetOrder = activeOrders.find((o) => o.tableId === targetTableId) ?? null;
  if (!await ensureOrdersAreNotProcessingCredit([transferMode.sourceOrderId, targetOrder?.id])) {
    return;
  }
  if (await ensureOrderHasPendingApproval(targetOrder?.id)) {
    if (targetOrder?.id != null) showPendingApprovalBlockingToast(targetOrder.id);
    return;
  }
  const isFullTransfer =
    transferMode.selectedLines.length === transferableOrderlines.length
    && transferableOrderlines.every((line) => selectedQtyByLineId.get(line.id) === line.quantity);
  await handleExecuteTransferFull(targetTableId, targetOrder?.id ?? null, isFullTransfer);
}, [transferMode, activeOrders, transferableOrderlines, ensureOrderHasPendingApproval, ensureOrdersAreNotProcessingCredit, ensureTablesAreNotInUse, handleExecuteTransferFull, isTransferring, showPendingApprovalBlockingToast]);

// Releasing an empty table/TA order returns to the areas (tables) screen on success.
const handleCloseOrderClick = useCallback((orderId: number) => {
  void handleCloseOrder(orderId).then((closed) => {
    if (closed) {
      clearOrderSelection();
      setActiveTab(landingTabRef.current);
    }
  });
}, [handleCloseOrder, clearOrderSelection]);

const handlePayAndCloseClick = useCallback((orderId: number, paymentlineId?: number) => {
  void handlePayAndCloseIfDone(orderId, paymentlineId);
}, [handlePayAndCloseIfDone]);

const handleSendToKitchenClick = useCallback((orderId: number, options?: {
  skipPrintJobs?: boolean;
  markSkipped?: boolean;
  actionGroupId?: string;
  selectedOrderlineIds?: number[];
  selectedOrderlineQuantities?: Array<{ orderlineId: number; quantity: number }>;
  expectedPrinterRoutes?: Array<{ printerId: number; printerName: string; orderlineIds: number[] }>;
  optimisticSentLineIds?: number[];
}) => {
  void handleSendToKitchen(orderId, options);
}, [handleSendToKitchen]);

const handleIncrementOrderlineClick = useCallback((line: OrderlineView) => {
  void handleIncrementOrderline(line);
}, [handleIncrementOrderline]);

const handleDecrementOrderlineClick = useCallback((line: OrderlineView) => {
  void handleDecrementOrderline(line);
}, [handleDecrementOrderline]);

const handleSetOrderlineQuantityClick = useCallback((line: OrderlineView, quantity: number) => {
  void handleSetOrderlineQuantity(line, quantity);
}, [handleSetOrderlineQuantity]);

const proceedOpenPaymentScreen = useCallback((orderId: number, options?: { tipOnly?: boolean }) => {
  void handleOpenPaymentScreen(orderId, options);
}, [handleOpenPaymentScreen]);

const minOrderReminder = useMinOrderReminder({
  tenantId,
  employeeSession,
  activeAreas,
  activeOrders,
  proceed: proceedOpenPaymentScreen,
});

const handleOpenPaymentScreenClick = useCallback((orderId: number, options?: { tipOnly?: boolean }) => {
  void minOrderReminder.requestOpenPayment(orderId, options);
}, [minOrderReminder]);

const [isSavingMinimum, setIsSavingMinimum] = useState(false);
const handleSaveTableMinimum = useCallback(
  async (areaId: number, tableId: number, payload: MinimumOrderPayload): Promise<boolean> => {
    if (!employeeSession || isSavingMinimum) return false;
    setIsSavingMinimum(true);
    try {
      await areasService.updateTable(areaId, tableId, payload, employeeSession);
      return true;
    } catch {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'עדכון מינימום ההזמנה נכשל', autoClose: 3000 });
      return false;
    } finally {
      setIsSavingMinimum(false);
    }
  },
  [employeeSession, isSavingMinimum],
);

const selectedTableMinimum = useMemo(() => {
  const order = activeOrders.find((entry) => entry.id === selectedOrderId);
  if (!order || order.tableId == null) {
    return null;
  }
  const table = activeAreas.flatMap((area) => area.tables).find((entry) => entry.id === order.tableId);
  return table ? { minimumOrder: table.minimumOrder, minimumOrderPerDiner: table.minimumOrderPerDiner } : null;
}, [activeAreas, activeOrders, selectedOrderId]);

const handleOpenReceiptScreenClick = useCallback((orderId: number) => {
  handleOpenReceiptScreen(orderId);
}, [handleOpenReceiptScreen]);

const handleOrdersMenuScreen = useCallback(() => {
  setIsPaymentScreenOpen(false);
  setIsReceiptScreenOpen(false);
}, [setIsPaymentScreenOpen, setIsReceiptScreenOpen]);

const handleReturnHomeClick = useCallback(() => {
  setIsGuestNameEditOpen(false);
  clearOrderSelection();
  setActiveTab(landingTabRef.current);
}, [clearOrderSelection, setActiveTab]);

const handleSelectOrderFromList = useCallback((order: OrderView) => {
  if (stationWaiter.intercept({ kind: 'order', order })) {
    return;
  }
  setIsGuestNameEditOpen(false);
  setSelectedOrderId(order.id);
  setSelectedOrderTableId(order.tableId);
  setSelectedOrderTableLabel(order.tableNumber);
}, [stationWaiter.intercept, setSelectedOrderId, setSelectedOrderTableId, setSelectedOrderTableLabel]);

// בחירת מלצר בעמדה נייחת: after the pick, resume the intercepted entry (bypass consumes one pass).
stationWaiterProceedRef.current = (action: StationWaiterAction) => {
  if (action.kind === 'table') {
    continueTableClick(action.tableId, action.tableLabel);
  } else if (action.kind === 'order') {
    handleSelectOrderFromList(action.order);
  } else if (action.kind === 'take_away') {
    void handleOpenTakeAwayClick();
  } else if (action.kind === 'kiosk_dine_in') {
    void handleOpenKioskDineIn();
  } else {
    void quickCharge.start();
  }
};

// The acting waiter holds through the order/payment (incl. quick-charge modals); cleared on exit.
useEffect(() => {
  if (activeTab !== 'orders' && !isPaymentScreenOpen && quickCharge.step === 'closed') {
    stationWaiter.clearActingWaiter();
  }
}, [activeTab, isPaymentScreenOpen, quickCharge.step, stationWaiter.clearActingWaiter]);

const stationWaiterPickerOptions = useMemo(
  () => (stationWaiter.isPickerOpen
    ? sortEmployeesForPicker(availableEmployees).map((employee) => ({ id: employee.id, name: employee.fullName, photoUrl: employee.photoUrl }))
    : null),
  [stationWaiter.isPickerOpen, availableEmployees],
);

const handleCloseSessionClick = useCallback(
  (payload?: { cashDrawerPrinterId?: number; closingCash?: number; nextOpeningCash?: number }): Promise<SessionCloseOutcome> => handleCloseSession(payload),
  [handleCloseSession],
);

const handleGoToOrdersFromCloseSession = useCallback(() => {
  setSettingsModalKey(null);
  clearOrderSelection();
  setActiveTab('orders');
}, [setSettingsModalKey, clearOrderSelection, setActiveTab]);

const handleRefreshProductsAndPrinters = useCallback(() => {
  void loadSettingsProducts();
  void loadActivePrinters();
}, [loadActivePrinters, loadSettingsProducts]);

// Architectural summary: Download the tenant catalog as an .xlsx via the manager export endpoint.
const {
  isImportOpen,
  openImport,
  closeImport,
  exportProducts: handleExportProducts,
  handleImported,
} = useProductImportExport({ tenantId, employeeSession, reloadVariantComboCounts, onError: setError });

// Architectural summary: Load categories when the manager opens catalog settings or Happy Hour setup.
// Details: docs/function-library/features/happy-hour.md#setup-modal--step-1-category-selection-2-step-wizard
useEffect(() => {
  if (
    (settingsModalKey !== 'categories' && settingsModalKey !== 'products' && !isHappyHourSetupOpen) ||
    !isManager ||
    !employeeSession
  ) {
    return;
  }
  let cancelled = false;
  void categoriesService
    .listForSettings(tenantId, employeeSession)
    .then((list) => {
      if (!cancelled) setSettingsCategories(list);
    })
    .catch((loadError: unknown) => {
      if (loadError instanceof Error) setError(loadError.message);
    });
  return () => {
    cancelled = true;
  };
}, [settingsModalKey, isHappyHourSetupOpen, isManager, employeeSession, tenantId]);

const handleRefreshPrintersClick = useCallback(() => {
  void loadActivePrinters();
}, [loadActivePrinters]);

const handleRefreshPrintJobsClick = useCallback(() => {
  void loadPrintJobs();
}, [loadPrintJobs]);

const handleDeactivateProductClick = useCallback((productId: number) => {
  void handleDeactivateProduct(productId);
}, [handleDeactivateProduct]);

const handleActivateProductClick = useCallback((productId: number) => {
  void handleActivateProduct(productId);
}, [handleActivateProduct]);

const handleRefreshEmployeesClick = useCallback(() => {
  void loadSettingsEmployees();
}, [loadSettingsEmployees]);

const handleDeactivateEmployeeClick = useCallback((employee: EmployeeView) => {
  void deactivateEmployee(employee);
}, [deactivateEmployee]);

const handleActivateEmployeeClick = useCallback((employee: EmployeeView) => {
  void activateEmployee(employee);
}, [activateEmployee]);

const handleTopBarTabChange = useCallback((tab: PosTabKey) => {
  // Any top-bar nav leaves the current order; clear selection so it can't block a deferred refresh.
  clearOrderSelection();
  closePostPaymentScreen();
  setPastReceiptOrderId(null);
  setActiveTab(isKioskMode && tab === 'areas' ? 'orders' : tab);
}, [clearOrderSelection, closePostPaymentScreen, isKioskMode, setPastReceiptOrderId, setActiveTab]);

const handleIdentificationRefresh = useCallback(() => {
  return loadAvailableEmployees();
}, [loadAvailableEmployees]);

const handleClearPosError = useCallback(() => {
  setError(null);
}, [setError]);

const handleOpenKdsFromIdentification = useCallback(() => {
  navigate(`/t/${tenantId}/kds`, { state: { from: `/t/${tenantId}/backoffice` } });
}, [navigate, tenantId]);

const handleCreatePrinterTestPrintClick = useCallback(() => {
  void handleCreatePrinterTestPrint();
}, [handleCreatePrinterTestPrint]);

const handleUpdatePrintJobStatusClick = useCallback((jobId: number, status: 'queued' | 'claimed' | 'printed' | 'failed') => {
  void handleUpdatePrintJobStatus(jobId, status);
}, [handleUpdatePrintJobStatus]);


if (!currentEmployee || !employeeSession) {
  return (
    <>
      <OfflineBanner isOffline={isOffline} />
      <EmployeeIdentificationGate
        employees={availableEmployees}
        isLoadingEmployees={isLoadingEmployees}
        isIdentifying={isIdentifying}
        isAttendanceActioning={isAttendanceActioning}
        error={null}
        tenantPhotoUrl={tenantPhotoUrl}
        onIdentify={handleIdentifyDirect}
        onAttendanceAction={handleAttendanceActionFromGate}
        onResolveAttendanceShiftStatus={handleResolveAttendanceShiftStatus}
        attendanceUiEnabled={attendanceUiEnabled}
        onRefresh={handleIdentificationRefresh}
        onClearError={handleClearPosError}
        onBeforeSelectEmployee={handleBeforeSelectEmployee}
        onOpenKds={handleOpenKdsFromIdentification}
      />
      <TakeoverConfirmModal
        conflict={takeoverConflict}
        isSubmitting={isTakingOver}
        onConfirm={handleTakeoverConfirm}
        onCancel={() => { setTakeoverConflict(null); setTakeoverPinOpen(false); }}
      />
      <PinPadModal
        isOpen={takeoverPinOpen}
        title="קוד עובד"
        subjectName={takeoverConflict?.employeeName ?? ''}
        isSubmitting={isTakingOver}
        error={null}
        onClose={() => setTakeoverPinOpen(false)}
        onSubmit={handleTakeoverPinSubmit}
      />
      <PickListModal
        title="בחר מדפסת"
        options={attendancePrintCandidates?.map((printer) => ({ id: printer.id, name: printer.name })) ?? null}
        disabled={attendancePrintInFlightRef.current}
        dataTestId="attendance-login-printer-pick"
        optionColor="primary"
        onPick={(printerId) => { void printPendingAttendanceAction(printerId); }}
        onClose={() => {
          setAttendancePrintCandidates(null);
          setPendingAttendancePrintAction(null);
        }}
      />
      <Modal
        opened={isAttendancePrintPromptOpen && pendingAttendancePrintAction !== null}
        onClose={handleCancelAttendancePrintPrompt}
        title={<strong>להדפיס אישור נוכחות?</strong>}
        closeButtonProps={{ 'data-testid': 'attendance-print-confirm-close' } as Record<string, string>}
        centered
        radius="md"
        classNames={{ root: navyModal.root }}
        closeOnClickOutside={!isResolvingAttendancePrintChoice && !attendancePrintInFlightRef.current}
        closeOnEscape={!isResolvingAttendancePrintChoice && !attendancePrintInFlightRef.current}
      >
        <div className={layoutClasses.modalBodyStack}>
          <div style={{ direction: 'rtl', textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fff' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>אישור נוכחות</div>
            <div>עובד: {pendingAttendancePrintAction?.employeeName ?? '--'}</div>
            <div>פעולה: {pendingAttendancePrintAction?.actionType === 'clock_out' ? 'יציאה ממשמרת' : 'כניסה למשמרת'}</div>
            <div>תאריך: {pendingAttendancePrintAction ? new Date(pendingAttendancePrintAction.actionDate).toLocaleString('he-IL') : '--'}</div>
          </div>
          <div className={layoutClasses.modalActionsRow}>
            <Button
              variant="cancel"
              onClick={handleCancelAttendancePrintPrompt}
              disabled={isResolvingAttendancePrintChoice || attendancePrintInFlightRef.current}
              dataTestId="attendance-print-confirm-cancel"
            >
              ביטול
            </Button>
            <Button
              variant="confirm"
              onClick={() => { void handleConfirmAttendancePrintPrompt(); }}
              loading={isResolvingAttendancePrintChoice || attendancePrintInFlightRef.current}
              dataTestId="attendance-print-confirm-ok"
            >
              הדפס אישור
            </Button>
          </div>
        </div>
      </Modal>
      <div style={{ position: 'fixed', left: -10000, top: 0, width: 320 }} aria-hidden="true">
        <div ref={attendancePrintRef} style={{ direction: 'rtl', fontFamily: 'Arial, sans-serif', padding: 12, background: '#fff', textAlign: 'center' }}>
          <div style={{ textAlign: 'center', fontWeight: 700, marginBottom: 8 }}>אישור נוכחות</div>
          <div>עובד: {pendingAttendancePrintAction?.employeeName ?? '--'}</div>
          <div>פעולה: {pendingAttendancePrintAction?.actionType === 'clock_out' ? 'יציאה ממשמרת' : 'כניסה למשמרת'}</div>
          <div>תאריך: {pendingAttendancePrintAction ? new Date(pendingAttendancePrintAction.actionDate).toLocaleString('he-IL') : '--'}</div>
        </div>
      </div>
    </>
  );
}

return (
  <section className={layoutClasses.shell}>
    <OfflineBanner isOffline={isOffline} />
    <PosTopBar
      tabs={visibleTabs}
      activeTab={
        activeTab === 'areas' || activeTab === 'orders' || activeTab === 'limit-items'
          ? activeTab
          : activeTab === 'table-history'
            ? 'orders'
            : null
      }
      tabBadges={isManager ? { orders: pendingApprovalIds.size } : undefined}
      onTabChange={handleTopBarTabChange}
      employeeName={currentEmployee.fullName}
      employeePhotoUrl={currentEmployeePhotoUrl}
      onSwitchEmployee={handleSwitchEmployee}
      onOpenSettings={() => setSettingsHubOpen(true)}
      canOpenSettings={true}
    />

    {activeTab === 'table-history' && tableHistoryTarget && employeeSession && (
      restoreSourceOrder ? (
        <PosRestoreBillSection
          tenantId={tenantId}
          employeeSession={employeeSession}
          isManager={isManager}
          sourceOrder={restoreSourceOrder}
          onBack={closeRestoreBill}
          onPrintReceipt={() => setPastReceiptOrderId(restoreSourceOrder.id)}
          onRestore={handleRestoreRequest}
          onSaveGuestName={handleSaveGuestName}
        />
      ) : (
        <PosTableHistorySection
          tenantId={tenantId}
          employeeSession={employeeSession}
          tableId={tableHistoryTarget.tableId}
          tableLabel={tableHistoryTarget.tableLabel}
          onSelectOrder={handleSelectHistoryOrder}
        />
      )
    )}

    {activeTab === 'areas' && (!isKioskMode || areaMode === 'edit') && (
      <PosAreasSection
        tenantId={tenantId}
        floorBackground={floorBackground}
        onFloorBackgroundChange={handleFloorBackgroundChange}
        selectedAreaList={selectedAreaList}
        selectedAreaId={selectedAreaId}
        onSelectArea={setSelectedAreaId}
        areaMode={areaMode}
        isManager={isManager}
        viewArea={viewArea}
        isLoadingAreas={isLoadingAreas}
        viewCanvasHostRef={viewCanvasHostRef}
        viewAreaCanvasBounds={viewAreaCanvasBounds}
        viewAreaCanvasScale={viewAreaCanvasScale}
        activeOrders={activeOrders}
        onViewTableClick={handleAreasTableClick}
        onOpenTakeAway={handleOpenTakeAwayClick}
        editingTableId={editingTableId}
        editArea={editArea}
        onToggleAreaActive={handleAreaActiveToggle}
        editCanvasHostRef={editCanvasHostRef}
        editAreaCanvasBounds={editAreaCanvasBounds}
        alignmentGuides={alignmentGuides}
        alignmentGapHints={alignmentGapHints}
        onBeginCanvasInteraction={beginCanvasInteraction}
        onStartEditTable={startEditTable}
        selectedEditTableIds={selectedTableIds}
        onSelectEditTable={handleSelectEditTable}
        onSelectEditTables={handleSelectEditTables}
        isSizeMasterPickMode={isSizeMasterPickMode}
        onToggleSizeMasterPickMode={() => setIsSizeMasterPickMode((previous) => !previous)}
        onPickSizeMasterTable={(tableId) => {
          void handleResizeSelectedTablesFromMaster(tableId);
          setIsSizeMasterPickMode(false);
        }}
        onAlignSelectedTablesRow={() => { void handleAlignSelectedTablesRow(); }}
        onAlignSelectedTablesColumn={() => { void handleAlignSelectedTablesColumn(); }}
        onExitEditMode={handleExitEditMode}
        onDeleteTable={() => { void handleDeleteTable(); }}
        onRenameTable={(name) => { void handleRenameTable(name); }}
        onChangeShape={(shape) => { void handleChangeShape(shape); }}
        onDuplicateTable={() => { void handleDuplicateTable(); }}
        onAddArea={(name) => { void handleCreateAreaByName(name); }}
        onRenameArea={(areaId, name) => { void handleRenameAreaByName(areaId, name); }}
        onUpdateAreaFloorDecor={(areaId, floorDecor) => { void handleUpdateAreaFloorDecor(areaId, floorDecor); }}
        onReorderAreas={(areaId, otherAreaId) => { void handleReorderAreas(areaId, otherAreaId); }}
        onAddTable={(number, shape, positionX, positionY, width, height) => { void handleAddTable(number, shape, positionX, positionY, width, height); }}
        transferMode={transferMode}
        onTransferTableLongPress={(tableId, tableLabel, orderId) => { void handleTransferLongPress(tableId, tableLabel, orderId); }}
        onCancelTransfer={handleCancelTransfer}
        onOpenDailyBrief={dailyBrief.open}
        hasUnreadDailyBrief={dailyBrief.hasUnread}
        onCancelTableLongPress={handleCancelTableLongPress}
        onPrintBillLongPress={(orderId) => { void handleOpenBillPreview(orderId); }}
        onItemActionsLongPress={(orderId) => { void handleItemActionsLongPress(orderId); }}
        onPickTargetTable={(tableId, tableLabel) => { void handlePickTargetTable(tableId, tableLabel); }}
        mergeMode={mergeMode}
        activeMergeLinks={activeMergeLinks}
        onMergeTableLongPress={handleMergeLongPress}
        onTableHistoryLongPress={handleTableHistoryLongPress}
        restorePickActive={restoreRequest !== null}
        onPickRestoreTable={(tableId) => { void handlePickRestoreTable(tableId); }}
        onCancelRestorePick={handleCancelRestorePick}
        onCancelMerge={handleCancelMerge}
        onToggleMergeTable={handleToggleMergeTableWithApprovalGuard}
        onConfirmMerge={() => { void handleConfirmMergeWithTablePresence(); }}
        splitMergeOrderPk={splitMergeOrderPk}
        isSplittingMerge={isSplittingMerge}
        onBeginSplitMerge={handleBeginSplitMergeWithApprovalGuard}
        onCancelSplitMerge={handleCancelSplitMerge}
        onConfirmSplitMerge={(linkIds) => { void handleConfirmSplitMergeWithTablePresence(linkIds); }}
        mergeConflict={mergeConflict}
        onMergeConflictCancel={handleMergeConflictCancel}
        onMergeConflictConfirm={() => { if (isManager) void handleMergeConflictConfirm(); }}
        setMinimumMode={isManager && isSetMinimumMode}
        onExitSetMinimum={() => setIsSetMinimumMode(false)}
        onSaveTableMinimum={handleSaveTableMinimum}
        isSavingMinimum={isSavingMinimum}
      />
    )}

    {minOrderReminder.reminder && (
      <MinOrderReminderDialog
        opened
        tableNumber={minOrderReminder.reminder.tableNumber}
        effectiveMinimum={minOrderReminder.reminder.effectiveMinimum}
        currentTotal={minOrderReminder.reminder.currentTotal}
        missing={minOrderReminder.reminder.missing}
        isBusy={minOrderReminder.isBusy}
        onAddCompletion={() => { void minOrderReminder.addCompletion(); }}
        onContinueWithout={minOrderReminder.continueWithout}
        onDismiss={minOrderReminder.dismiss}
      />
    )}

    {activeTab === 'limit-items' && (
      <PosLimitItemsSection
        posProducts={posProducts}
        isLoadingPosProducts={isLoadingPosProducts}
        employeeSession={employeeSession}
        isManager={isManager}
        onLimitSaveError={setError}
      />
    )}

    {activeTab === 'orders' && (
      <>
        {isKioskMode && selectedOrderId === null && selectedOrderTableId === null && (
          <KioskOrderTypeScreen
            isOpening={isOpeningOrder}
            onDineIn={() => { void handleOpenKioskDineIn(); }}
            onTakeAway={() => { void handleOpenTakeAwayClick(); }}
          />
        )}
        <PosOrdersSection
          tenantId={tenantId}
          employeeSession={employeeSession}
          selectedOrderTableId={selectedOrderTableId}
          selectedOrderTableLabel={selectedOrderTableLabel}
          selectedOrderId={selectedOrderId}
          isManager={isManager}
          currentEmployeeName={currentEmployee?.fullName ?? null}
          actingWaiterName={stationWaiter.actingWaiter?.name ?? null}
          ordersPageWaiterOwnVisibility={ordersPageWaiterOwnVisibility}
          manualCreditEntryBlocked={manualCreditEntryBlocked}
          cashPaymentBlocked={cashCloseManagerOnly && !isManager}
          activeOrders={activeOrders}
          orderlines={orderlines}
          paymentlines={paymentlines}
          posProducts={posProducts}
          posCategories={posCategories}
          activeAreas={activeAreas}
          areaPrintRules={areaPrintRules}
          happyHour={happyHour}
          happyHourEffective={happyHourEffective}
          isPaymentScreenOpen={isPaymentScreenOpen}
          isReceiptScreenOpen={isReceiptScreenOpen}
          isLoadingPaymentlines={isLoadingPaymentlines}
          isPreparingPaymentline={isPreparingPaymentline}
          isMarkingPaymentDone={isMarkingPaymentDone}
          isSplittingPayment={isSplittingPayment}
          isSettingTip={isSettingTip}
          lockedCreditPaymentlineIds={lockedCreditPaymentlineIds}
          failedCreditPaymentlineIds={failedCreditPaymentlineIds}
          isLoadingPosProducts={isLoadingPosProducts}
          isAddingOrderline={isAddingOrderline}
          isAdjustingOrderline={isAdjustingOrderline}
          isSendingToKitchen={isSendingToKitchen}
          isLoadingOrderlines={isLoadingOrderlines}
          orderlinesLoadedOrderId={orderlinesLoadedOrderId}
          isClosingOrder={isClosingOrder}
          isOpeningOrder={isOpeningOrder}
          activeSession={activeSession}
          productSearch={productSearch}
          onProductSearchChange={setProductSearch}
          recentResultIds={recentResultIds}
          recentResultsVersion={recentResultsVersion}
          onClearRecentResults={clearRecentResults}
          onOpenOrder={handleOpenTableClick}
          onCloseOrder={handleCloseOrderClick}
          onPayAndCloseIfDone={handlePayAndCloseClick}
          onSettleCashOverage={handleSettleCashOverage}
          onCashChangeDialogOpenChange={setIsCashChangeDialogOpen}
          onRevertCashPayment={handleRevertCashPayment}
          onRecoverFailedCreditPaymentline={handleRecoverFailedCreditPaymentline}
          posTerminals={posTerminals}
          onChargeCredit={handleChargeCredit}
          onChargeManualCredit={handleOpenManualCredit}
          onChargeTapOnPhone={handleOpenTapOnPhone}
          onSplitAndPay={handleSplitAndPay}
          onSplitAndPayTapOnPhone={handleSplitAndPayTapOnPhone}
          onShowPartialReceipt={handleShowPartialReceipt}
          isTapOnPhoneAvailable={isTapOnPhoneAvailable}
          tenantIsTest={tenantIsTest}
          onSplitPayment={handleSplitPaymentline}
          onSetOrderTip={handleSetOrderTip}
          onSetPaymentlineTip={handleSetPaymentlineTip}
          onResetOrderTip={handleResetOrderTipAndLines}
          onAddOrderline={handleProductTap}
          onIncrementOrderline={handleIncrementOrderlineClick}
          onDecrementOrderline={handleDecrementOrderlineClick}
          onEditOrderlineSelections={handleEditSelections}
          onUpdateOrderlineNote={handleUpdateOrderlineNote}
          onSetOrderlineQuantity={handleSetOrderlineQuantityClick}
          onSendToKitchen={handleSendToKitchenClick}
          activePrinterIds={activePrinterIds}
          onRequestKitchenFireDispatch={requestKitchenFireDispatch}
          onOpenPaymentScreen={handleOpenPaymentScreenClick}
          onOpenReceiptScreen={handleOpenReceiptScreenClick}
          onOpenReceiptPreview={() => setIsReceiptPreviewOpen(true)}
          onEditGuestName={() => setIsGuestNameEditOpen(true)}
          onSaveGuestName={handleSaveGuestName}
          onMenuScreen={handleOrdersMenuScreen}
          onReturnHome={handleReturnHomeClick}
          onSelectOrderFromList={handleSelectOrderFromList}
          onUpdateDiners={handleUpdateDinersClick}
          onToggleDeposit={handleToggleOrderDeposit}
          onToggleOrderVip={handleToggleOrderVip}
          isUpdatingDiners={isUpdatingDiners}
          currentDinersCount={selectedTableOrder?.dinersCount}
          onTransfer={() => { void handleTransferFromOrdersPanel(); }}
          isTransferring={isTransferring}
          isMergedTable={activeMergeLinks.some(
            (link) => link.tablePk === selectedOrderTableId || link.orderPk === selectedOrder?.id,
          )}
          resolveComboSelectionRoute={resolveComboSelectionRoute}
          onApprovalSubmitted={refreshOrderlinesForCurrentOrder}
          approvalEventCallbackRef={approvalEventCallbackRef}
          cancelAllSentLinesCallbackRef={cancelAllSentLinesCallbackRef}
          paidActive={paidActive}
          paidOrders={paidOrders}
          isLoadingPaid={isLoadingPaid}
          onTogglePaid={togglePaid}
          onOpenPaidReceipt={(orderId) => setPastReceiptOrderId(orderId)}
          onPreviewBill={(orderId) => { void handleOpenBillPreview(orderId); }}
          onItemActions={(orderId) => { void handleItemActionsLongPress(orderId); }}
          selectedTableMinimum={selectedTableMinimum}
          isKioskMode={isKioskMode}
        />
      </>
    )}

    <Modal
      opened={cancelTableConfirm !== null}
      onClose={() => {
        if (isCancelingTable) {
          return;
        }
        setCancelTableConfirm(null);
      }}
      title={<strong className={layoutClasses.cancelTableWarningTitle}>ביטול שולחן</strong>}
      closeButtonProps={{ 'data-testid': 'cancel-table-close' } as Record<string, string>}
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeOnClickOutside={!isCancelingTable}
      closeOnEscape={!isCancelingTable}
    >
      <div className={layoutClasses.modalBodyStack}>
        <p>
          פעולה זו תבטל את ההזמנה כולה, האם להמשיך?
        </p>
        <div className={layoutClasses.modalActionsRow}>
          <Button variant="confirm" onClick={() => setCancelTableConfirm(null)} disabled={isCancelingTable} dataTestId="cancel-table-keep">
            שמור הזמנה
          </Button>
          <Button variant="cancel" onClick={() => { void handleConfirmCancelTable(); }} disabled={isCancelingTable} dataTestId="cancel-table-confirm">
            כן, בטל הזמנה
          </Button>
        </div>
      </div>
    </Modal>

    {cancelTableApprovalRequest && (
      <ApprovalRequestModal
        opened={true}
        onClose={() => {
          if (!isSubmittingCancelTableApproval) {
            setCancelTableApprovalRequest(null);
          }
        }}
        actionType="cancel"
        payload={buildCancelApprovalPayload(cancelTableApprovalRequest.items, cancelTableApprovalRequest.cancelScope)}
        orderlines={cancelTableApprovalRequest.orderlines}
        isSubmitting={isSubmittingCancelTableApproval}
        isManager={isManager}
        onSubmit={handleSubmitCancelTableApproval}
      />
    )}

    <Modal
      opened={paymentOveragePrompt !== null}
      onClose={() => {
        if (isResolvingPaymentOverage) {
          return;
        }
        dismissPaymentOverageToMenu();
      }}
      title={<strong>עודף בתשלום</strong>}
      closeButtonProps={{ 'data-testid': 'payment-overage-close' } as Record<string, string>}
      centered
      radius="md"
      closeOnClickOutside={!isResolvingPaymentOverage}
      closeOnEscape={!isResolvingPaymentOverage}
    >
      <div className={layoutClasses.modalBodyStack}>
        <p>עודף: {formatMoney(paymentOveragePrompt?.overpaidAmount ?? 0)} ₪</p>
        <div className={layoutClasses.modalActionsRow}>
          <Button variant="cancel" onClick={dismissPaymentOverageToMenu} disabled={isResolvingPaymentOverage} dataTestId="payment-overage-back">
            חזרה לתפריט
          </Button>
          <Button variant="confirm" onClick={() => { void confirmPaymentOverageClose(); }} loading={isResolvingPaymentOverage} dataTestId="payment-overage-close">
            סגור הזמנה
          </Button>
        </div>
      </div>
    </Modal>

    <PosReportModal
      opened={reports.singleReportOpen}
      onClose={reports.closeSingleReport}
      report={reports.singleReport}
      loading={reports.loadingReport}
      title={reports.singleReportTitle}
      tenantId={tenantId}
      employeeSession={employeeSession ?? ''}
    />

    <PosReportRangeModal
      mode={reports.rangeMode}
      onClose={reports.closeRangeModal}
      tenantId={tenantId}
      employeeSession={employeeSession ?? ''}
      onReportReady={reports.openReportView}
    />

    <PosSessionPickerModal
      opened={reports.zcreditSessionPickerOpen}
      onClose={reports.closeZcreditSessionPicker}
      title="בחר משמרת"
      showAmount={false}
      sessions={reports.zcreditSessionOptions.map((option) => ({
        id: option.id,
        name: option.name,
        openTime: option.date,
        closeTime: null,
        state: option.state,
        totalIncome: 0,
      }))}
      onPick={(session) => reports.pickZcreditSession({ id: session.id, name: session.name })}
    />

    <ZReportLayoutModal
      opened={reports.zLayoutOpen}
      loading={reports.zLayoutLoading}
      saving={reports.zLayoutSaving}
      initialConfig={reports.zLayoutConfig}
      previewReport={reports.singleReport?.kind === 'z' ? reports.singleReport : null}
      onClose={reports.closeZLayout}
      onSave={reports.saveZLayout}
    />

    <ZCreditTransactionsModal
      opened={reports.zcreditSelectedSession !== null}
      onClose={reports.closeZcreditTransactions}
      onBack={reports.backToZcreditSessionPicker}
      tenantId={tenantId}
      sessionId={reports.zcreditSelectedSession?.id ?? null}
      sessionName={reports.zcreditSelectedSession?.name ?? null}
      employeeSession={employeeSession ?? ''}
    />

    <AccountantReportModal
      opened={accountantReportOpen}
      onClose={() => setAccountantReportOpen(false)}
      tenantId={tenantId}
      employeeSession={employeeSession ?? ''}
    />

    <CashDrawerPickModal
      prompt={cashDrawerPrompt}
      disabled={isMarkingPaymentDone}
      dataTestId="cash-drawer-pick"
      onPick={(drawerId) => { void confirmCashDrawerSelection(drawerId); }}
      onClose={dismissCashDrawerPrompt}
    />

    <CashDrawerPickModal
      prompt={manualDrawerPrompt}
      disabled={isOpeningManualDrawer}
      dataTestId="manual-drawer-pick"
      onPick={(drawerId) => { void openCashDrawerById(drawerId); }}
      onClose={() => {
        if (isOpeningManualDrawer) {
          return;
        }
        setManualDrawerPrompt(null);
      }}
    />

    <Modal
      opened={paymentZeroTotalPrompt !== null}
      onClose={() => {
        if (isResolvingZeroTotalClose) {
          return;
        }
        dismissZeroTotalToMenu();
      }}
      title={<strong>{paymentZeroTotalPrompt?.reason === 'settled' ? 'התשלום הושלם' : 'הזמנה על 0'}</strong>}
      closeButtonProps={{ 'data-testid': 'payment-zero-total-close' } as Record<string, string>}
      centered
      radius="md"
      closeOnClickOutside={!isResolvingZeroTotalClose}
      closeOnEscape={!isResolvingZeroTotalClose}
    >
      <div className={layoutClasses.modalBodyStack}>
        <p>
          {paymentZeroTotalPrompt?.reason === 'settled'
            ? 'כל התשלומים הושלמו. לסגור את ההזמנה או לחזור לתפריט?'
            : 'ההזמנה היא על 0. לסגור על 0 או לחזור לתפריט?'}
        </p>
        <div className={layoutClasses.modalActionsRow}>
          <Button variant="cancel" onClick={dismissZeroTotalToMenu} disabled={isResolvingZeroTotalClose} dataTestId="zero-total-back">
            חזרה לתפריט
          </Button>
          <Button variant="confirm" onClick={() => { void confirmZeroTotalClose(); }} loading={isResolvingZeroTotalClose} dataTestId="zero-total-close">
            {paymentZeroTotalPrompt?.reason === 'settled' ? 'סגור הזמנה' : 'סגור על 0'}
          </Button>
        </div>
      </div>
    </Modal>

    <NumPadModal
      isOpen={isDinersNumPadOpen}
      dataTestId="order-open-diners-numpad"
      title="מספר סועדים"
      subjectName="סועדים"
      allowUnlimited={false}
      minValue={1}
      maxDigits={2}
      confirmLabel="פתח שולחן"
      isSubmitting={isOpeningOrder}
      onClose={() => { pendingMergeAfterDinersRef.current = false; setIsDinersNumPadOpen(false); }}
      onConfirm={handleDinersConfirm}
    />

    <PickListModal
      title="בחירת מלצר"
      options={stationWaiterPickerOptions}
      dataTestId="station-waiter-pick"
      optionColor="primary"
      variant="employee-grid"
      fullScreen
      onPick={(employeeId) => {
        const found = availableEmployees.find((employee) => employee.id === employeeId);
        if (found) {
          stationWaiter.pickWaiter(found);
        }
      }}
      onClose={stationWaiter.closePicker}
    />

    <ManualCreditDialog
      isOpen={manualCreditTarget !== null}
      terminalLabel="אשראי ידני"
      amountLabel={
        manualCreditTarget
          ? `${formatMoney(
            manualCreditTarget.splitTotal
            ?? manualCreditTarget.lineFullTotal
            ?? paymentlines.find((line) => line.id === manualCreditTarget.paymentlineId)?.amount
            ?? 0,
          )} ₪`
          : ''
      }
      isSubmitting={isMarkingPaymentDone}
      onClose={() => setManualCreditTarget(null)}
      onSubmit={handleSubmitManualCredit}
    />

    <TapOnPhoneDialog
      isOpen={tapOnPhoneTarget !== null}
      amountLabel={
        tapOnPhoneTarget
          ? `${formatMoney(resolvePaymentlineChargeAmount(tapOnPhoneTarget.paymentlineId))} ₪`
          : ''
      }
      onClose={() => setTapOnPhoneTarget(null)}
      onStart={handleStartTapOnPhone}
    />

    <ItemDiscountSelectModal
      isOpen={transferMode.step === 'item-select'}
      title="בחר פריטים להעברה"
      confirmLabel="המשך"
      showSteps={false}
      hideCloseButton
      orderlines={transferableOrderlines}
      isSelectableLine={(line) => line.status === 'sent' || line.status === 'skipped'}
      isLockedIncludedLine={isFullyPaidLine}
      getSelectableQuantity={getSelectableItemPaymentQuantity}
      resolveSubtotal={computeItemPaymentSplitAmount}
      maxSubtotal={transferRemainingToPay ?? undefined}
      overLimitMessage="לא ניתן להעביר מוצרים בסכום גבוה מהיתרה לתשלום"
      allowFullSelectionOverride
      onConfirm={(selections) => {
        const selectedQtyByLineId = new Map(selections.map((selection) => [selection.orderlineId, selection.quantity]));
        const hasPaidSelection = transferableOrderlines.some(
          (line) => isFullyPaidLine(line) && selectedQtyByLineId.has(line.id),
        );
        const isFullTransferSelection =
          selections.length === transferableOrderlines.length
          && transferableOrderlines.every((line) => selectedQtyByLineId.get(line.id) === line.quantity);

        if (hasPaidSelection && !isFullTransferSelection) {
          notifications.show({
            color: 'red',
            title: 'לא ניתן להעביר פריט',
            message: 'לא ניתן להעביר פריט ששולם מבלי להעביר את כלל השולחן.',
            autoClose: 4000,
          });
          return;
        }

        handleTransferItemsSelected(selections.map((selection) => ({
          orderlineId: selection.orderlineId,
          quantity: selection.quantity,
        })));
        setActiveTab('areas');
        setTransferRemainingToPay(null);
      }}
      onClose={() => { setTransferRemainingToPay(null); handleCancelTransfer(); }}
    />

    <DailyBriefModal
      opened={dailyBrief.isOpen}
      onClose={dailyBrief.close}
      dailyBrief={activeSession?.dailyBrief ?? null}
      isManager={isManager}
      onSave={dailyBrief.save}
      isSaving={dailyBrief.isSaving}
    />

    {guestNameDialogOrder && (
      <GuestNameEditModal
        order={guestNameDialogOrder}
        opened={isGuestNameEditOpen}
        onClose={() => setIsGuestNameEditOpen(false)}
        onSave={handleSaveGuestName}
        isSaving={isSavingGuestName}
      />
    )}

    {receiptTableOrder && (
      <ReceiptPrintPreviewModal
        opened={isReceiptPreviewOpen}
        onClose={() => setIsReceiptPreviewOpen(false)}
        order={receiptTableOrder}
        orderlines={orderlines}
        paymentlines={paymentlines}
      />
    )}

    {itemActionsOrder && (
      <OrderItemActionsFlow
        tenantId={tenantId}
        employeeSession={employeeSession}
        activeSessionId={activeSession?.id ?? null}
        isManager={isManager}
        order={itemActionsOrder}
        onClose={closeItemActions}
      />
    )}

    {billPreview && (
      <ReceiptPrintPreviewModal
        opened
        onClose={() => setBillPreview(null)}
        order={billPreview.order}
        orderlines={billPreview.orderlines}
        paymentlines={billPreview.paymentlines}
      />
    )}

    {autoPrintElements}

    {kitchenPrintElements}
    {kitchenFirePrintElements}

    <AutoCloseZPrinter
      tenantId={tenantId}
      employeeSession={employeeSession}
      liveAutoClosedSessionId={autoClosedZPrint.sessionId || null}
      liveAutoClosedNonce={autoClosedZPrint.nonce}
      onInFlightChange={setIsAutoZPrintInFlight}
    />

    <QuickChargeMethodModal
      step={quickCharge.step === 'tender' || quickCharge.step === 'credit' ? quickCharge.step : 'closed'}
      terminals={posTerminals}
      isTapOnPhoneAvailable={isTapOnPhoneAvailable}
      manualCreditEntryBlocked={manualCreditEntryBlocked}
      cashPaymentBlocked={cashCloseManagerOnly && !isManager}
      onCash={quickCharge.selectCash}
      onCredit={quickCharge.selectCredit}
      onCreditMethod={quickCharge.selectCreditMethod}
      onBack={quickCharge.back}
      onClose={() => { void quickCharge.cancel(); }}
    />

    <ValueEntryModal
      isOpen={quickCharge.step === 'amount'}
      title="חיוב מהיר"
      amountOnly
      percentBase={0}
      inputLabels={{ percentage: 'אחוז', amount: 'סכום לחיוב' }}
      confirmLabel="אישור"
      dataTestId="quick-charge"
      isSaving={quickCharge.isSubmitting}
      headerSlot={(
        <SegmentedControl
          fullWidth
          value={quickCharge.mode}
          onChange={(value) => quickCharge.setMode(value as 'charge' | 'refund' | 'service' | 'service_refund')}
          data={[
            { value: 'charge', label: 'חיוב' },
            ...(quickCharge.tender?.kind === 'cash' || quickCharge.tender?.kind === 'pinpad'
              ? [{ value: 'refund', label: 'זיכוי' }]
              : []),
            { value: 'service', label: 'חיוב טיפ' },
            ...(quickCharge.tender?.kind === 'cash'
              ? [{ value: 'service_refund', label: 'זיכוי טיפ' }]
              : []),
          ]}
          data-testid="quick-charge-mode"
        />
      )}
      onClose={() => { void quickCharge.cancel(); }}
      onConfirm={(result) => { void quickCharge.confirmAmount(result.amount); }}
    />

    <ValueEntryModal
      isOpen={settingsModalKey === 'retroCountedCash'}
      title="עדכון מזומן נספר (רטרו)"
      amountOnly
      percentBase={0}
      inputLabels={{ percentage: 'אחוז', amount: 'מזומן נספר בקופה' }}
      confirmLabel="שמור"
      maxAmount={999999}
      capAmountMessage="הסכום חייב להיות בין 0 ל-999999"
      allowZero
      headerSlot={(
        <div style={{ direction: 'rtl', padding: '0 16px 10px', textAlign: 'right' }}>
          <Select
            label="משמרת לעדכון"
            placeholder={isLoadingRetroSessionOptions ? 'טוען משמרות...' : 'בחר משמרת'}
            value={retroSelectedSessionId != null ? String(retroSelectedSessionId) : null}
            data={retroSessionSelectData}
            onChange={(value) => {
              setRetroSelectedSessionId(value ? Number(value) : null);
            }}
            comboboxProps={{ withinPortal: false }}
            disabled={isLoadingRetroSessionOptions || isSavingRetroCountedCash}
            data-testid="retro-counted-cash-session-select"
          />
        </div>
      )}
      dataTestId="retro-counted-cash"
      isSaving={isSavingRetroCountedCash}
      onClose={() => {
        if (isSavingRetroCountedCash) {
          return;
        }
        setSettingsModalKey(null);
      }}
      onConfirm={(result) => {
        if (retroSelectedSessionId === null) {
          notifications.show({
            color: 'red',
            title: 'שגיאה',
            message: 'יש לבחור משמרת לפני שמירה',
            autoClose: 3000,
          });
          return;
        }
        void handleRetroSetCountedCash(retroSelectedSessionId, result.amount);
      }}
    />

    <PinPadModal
      isOpen={quickCharge.step === 'refund-code'}
      title="אימות זיכוי"
      subjectName={quickCharge.refundDeliveryTargetLabel ?? quickCharge.refundOwnerEmail ?? 'קוד נשלח לבעלים'}
      pinLength={6}
      isSubmitting={quickCharge.isSubmitting}
      onClose={() => { void quickCharge.cancel(); }}
      onSubmit={quickCharge.confirmRefundCode}
    />

    <SettingsHubModal
      opened={settingsHubOpen}
      onClose={() => setSettingsHubOpen(false)}
      onSelectTile={handleSelectSettingsTile}
      isManager={isManager}
      isSuperAdmin={isSuperAdmin}
      employeeName={currentEmployee.fullName}
      employeePhotoUrl={currentEmployeePhotoUrl}
      onSwitchEmployee={handleSwitchEmployee}
      autoCloseTime={autoCloseTime}
      happyHour={happyHour}
      fixedPrinterName={fixedPrinterName}
      restigoEnabled={restigoEnabled}
      kioskModeActive={isKioskMode}
      employeeAttendanceReportEnabled={employeeAttendanceReportEnabled}
      employeeShiftCloseEnabled={employeeShiftCloseEnabled}
      attendanceUiEnabled={attendanceUiEnabled}
      shiftCloseCashBalancesEnabled={shiftCloseCashBalancesEnabled}
    />

    <RestigoPushesModal
      opened={settingsModalKey === 'restigo'}
      onClose={() => setSettingsModalKey(null)}
      tenantId={tenantId}
      employeeSession={employeeSession}
    />

    {isManager && (
      <PosAssistantPanel
        opened={settingsModalKey === 'posAssistant'}
        onClose={() => setSettingsModalKey(null)}
        tenantId={tenantId}
        employeeSession={employeeSession}
      />
    )}

    <FixedPrinterModal
      opened={settingsModalKey === 'fixedPrinter'}
      onClose={() => setSettingsModalKey(null)}
      tenantId={tenantId}
      employeeSession={employeeSession ?? ''}
      fixedPrinterId={fixedPrinterId}
      onSelect={setFixedPrinter}
      onClear={clearFixedPrinter}
    />

    <PosSettingsModal
      opened={settingsModalKey === 'posSettings'}
      onClose={() => setSettingsModalKey(null)}
      viewportWidth={viewportWidth}
      isSubmitting={isSavingPosSettings}
      isSuperAdmin={isSuperAdmin}
      requiresManagerApproval={generalItemRequiresManagerApproval}
      onChangeRequiresManagerApproval={(next) => {
        void handleGeneralItemApprovalSettingChange(next);
      }}
      shiftCloseCashBalancesEnabled={shiftCloseCashBalancesEnabled}
      onChangeShiftCloseCashBalancesEnabled={(next) => {
        void handleShiftCloseCashBalancesSettingChange(next);
      }}
      ordersPageWaiterOwnVisibility={ordersPageWaiterOwnVisibility}
      onChangeOrdersPageWaiterOwnVisibility={(next) => {
        void handleOrdersPageWaiterOwnVisibilityChange(next);
      }}
      manualCreditEntryBlocked={manualCreditEntryBlocked}
      onChangeManualCreditEntryBlocked={(next) => {
        void handleManualCreditEntryBlockedChange(next);
      }}
      customerNameOnOrderEntry={customerNameOnOrderEntry}
      onChangeCustomerNameOnOrderEntry={(next) => {
        void handleCustomerNameOnOrderEntryChange(next);
      }}
      stationWaiterSelectionEnabled={stationWaiterSelectionEnabled}
      onChangeStationWaiterSelectionEnabled={(next) => {
        void handleStationWaiterSelectionEnabledChange(next);
  }}
      cashCloseManagerOnly={cashCloseManagerOnly}
      onChangeCashCloseManagerOnly={(next) => {
        void handleCashCloseManagerOnlyChange(next);
      }}
      employeeShiftCloseEnabled={employeeShiftCloseEnabled}
      onChangeEmployeeShiftCloseEnabled={(next) => {
        void handleEmployeeShiftCloseEnabledChange(next);
      }}
      refundCodeViaPush={refundCodeViaPush}
      onChangeRefundCodeViaPush={(next) => {
        void handleRefundCodeViaPushChange(next);
      }}
      employeeAttendanceReportEnabled={employeeAttendanceReportEnabled}
      onChangeEmployeeAttendanceReportEnabled={(next) => {
        void handleEmployeeAttendanceReportEnabledChange(next);
      }}
      attendanceUiEnabled={attendanceUiEnabled}
      onChangeAttendanceUiEnabled={(next) => { void handleAttendanceUiEnabledChange(next); }}
      rootClassName={navyModal.root}
    />

    {isManager && (
      <AreaPrintSettingsModal
        opened={settingsModalKey === 'areaPrintSettings'}
        onClose={() => setSettingsModalKey(null)}
        rules={areaPrintRules}
        onSaveRules={handleSaveAreaPrintRules}
        isSaving={isSavingAreaPrintRules}
        posProducts={posProducts}
        categories={posCategories}
        activePrinters={activePrinters}
        areas={activeAreas}
        viewportWidth={viewportWidth}
      />
    )}

    <AutoCloseSessionModal
      opened={settingsModalKey === 'autoCloseSession'}
      onClose={() => setSettingsModalKey(null)}
      tenantId={tenantId}
      employeeSession={employeeSession}
      autoCloseTime={autoCloseTime}
      sessionOpenTime={activeSession?.openTime ?? null}
      onSaved={setAutoCloseTime}
    />

    <EditVatRateModal
      tenant={vatEdit.isOpen ? vatEdit.tenant : null}
      isSaving={vatEdit.isSaving}
      onClose={vatEdit.close}
      onSubmit={(tenant, rate) => {
        void vatEdit.submit(tenant, rate);
      }}
    />

    <EditHeaderModal
      tenant={headerEdit.isOpen ? headerEdit.tenant : null}
      isSaving={headerEdit.isSaving}
      onClose={headerEdit.close}
      onSubmit={(tenant, header) => {
        void headerEdit.submit(tenant, header);
      }}
    />

    <HappyHourSetupModal
      opened={isHappyHourSetupOpen}
      onClose={closeHappyHourSetup}
      tenantId={tenantId}
      employeeSession={employeeSession}
      categories={settingsCategories.length > 0 ? settingsCategories : posCategories}
      happyHour={happyHour}
    />

    <HappyHourActiveModal
      opened={isHappyHourActiveOpen}
      onClose={closeHappyHourActive}
      tenantId={tenantId}
      employeeSession={employeeSession}
      happyHour={happyHour}
    />

    <OpenSessionModal
      isOpen={openSessionFlow.modalMode !== null}
      mode={openSessionFlow.modalMode ?? 'open'}
      isManager={isManager}
      isSaving={openSessionFlow.isSaving}
      existingAmount={openSessionFlow.modalMode === 'edit' ? (activeSession?.cashAlterations ?? 0) : nextOpeningCashSuggestion}
      onOpenDrawer={() => {
        void handleOpenCashDrawer();
      }}
      onConfirm={(openingCash) => {
        if (openSessionFlow.modalMode === 'edit') {
          void openSessionFlow.confirmEdit(openingCash, activeSession?.cashAlterations ?? 0);
        } else {
          void openSessionFlow.confirmOpen(openingCash);
        }
      }}
      onClose={openSessionFlow.closeModal}
    />

    <Modal
      opened={settingsModalKey === 'session'}
      onClose={() => {
        if (isSavingSession) {
          return;
        }
        setSettingsModalKey(null);
      }}
      withCloseButton={false}
      closeOnClickOutside={!isSavingSession}
      closeOnEscape={!isSavingSession}
      size="lg"
      centered
      radius="md"
      classNames={{ content: sessionCloseClasses.modalContent, body: sessionCloseClasses.modalBody }}
    >
      <SessionControlsSection
        activeSession={activeSession}
        isLoadingSession={isLoadingSession}
        isSavingSession={isSavingSession}
        hasEmployeeSession={Boolean(employeeSession)}
        shiftCloseCashBalancesEnabled={shiftCloseCashBalancesEnabled}
        orderCount={sessionSummary.orderCount}
        salesTotal={sessionSummary.salesTotal}
        openOrderCount={activeOrders.length}
        tenantId={tenantId}
        employeeSession={employeeSession ?? ''}
        onGoToOrders={handleGoToOrdersFromCloseSession}
        onCloseSession={handleCloseSessionClick}
        onCancel={() => setSettingsModalKey(null)}
        onZPrintInFlightChange={setIsManualZPrintInFlight}

      />
    </Modal>

    <Modal
      opened={settingsModalKey === 'products'}
      onClose={() => setSettingsModalKey(null)}
      title="ניהול מוצרים"
      closeButtonProps={{ 'data-testid': 'settings-products-close' } as Record<string, string>}
      size="xl"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
    >
      <ProductSettingsSection
        productFormRows={productFormRows}
        activePrinters={activePrinters}
        settingsProducts={settingsProducts}
        categories={settingsCategories}
        areaPrintRules={areaPrintRules}
        employeeSession={employeeSession}
        isSavingProduct={isSavingProduct}
        editingProductId={editingProductId}
        isLoadingProducts={isLoadingProducts}
        isLoadingPrinters={isLoadingPrinters}
        isTogglingProductStatus={isTogglingProductStatus}
        onChangeProductFormRowField={onChangeProductFormRow}
        onProductFormRowImageChange={onProductFormRowImageChange}
        onAddProductFormRow={onAddProductFormRow}
        onRemoveProductFormRow={onRemoveProductFormRow}
        onSaveProduct={handleSaveProduct}
        onCancelEdit={resetProductForm}
        onRefreshProducts={handleRefreshProductsAndPrinters}
        onExportProducts={handleExportProducts}
        onOpenImport={openImport}
        onEditProduct={startEditProduct}
        onOpenOptions={openProductOptions}
        variantComboCountByProductId={variantComboCountByProductId}
        onDeactivateProduct={handleDeactivateProductClick}
        onActivateProduct={handleActivateProductClick}
        onToggleProductKds={(productId, nextKds) => {
          void handleToggleProductKds(productId, nextKds);
        }}
        isProductKdsUpdating={isProductKdsUpdating}
        onCreateCategory={handleCreateCategoryInline}
        onBulkUpdateProducts={handleBulkUpdateProducts}
      />
    </Modal>

    <ImportProductsModal
      opened={isImportOpen}
      onClose={closeImport}
      tenantId={tenantId}
      employeeSession={employeeSession}
      onImported={handleImported}
    />

    <OptionsBuilderDrawer
      opened={optionsProduct !== null}
      productName={optionsProduct?.name ?? ''}
      productPrice={optionsProduct?.price ?? 0}
      currentProductId={optionsProduct?.id ?? null}
      productKds={optionsProduct?.kds ?? false}
      productPrinterIds={optionsProduct?.printerIds ?? []}
      groups={composition.groups}
      activePrinters={activePrinters}
      allProducts={settingsProducts}
      areaPrintRules={areaPrintRules}
      comboItemsByProductId={comboItemsByProductId}
      isLoading={composition.isLoadingComposition}
      isSaving={composition.isSavingComposition}
      isCopyingGroup={isCopyingGroup}
      onClose={closeProductOptions}
      onSave={(uiGroups) => { void handleSaveProductOptions(uiGroups); }}
      onCopyGroupToProducts={(group, targetProductIds, targetItemIds) => { void handleCopyGroupToProducts(group, targetProductIds, targetItemIds); }}
    />

    <CategoryManagerModal
      opened={settingsModalKey === 'categories'}
      onClose={() => setSettingsModalKey(null)}
      tenantId={tenantId}
      employeeSession={employeeSession}
      categories={settingsCategories}
      products={settingsProducts}
    />

    <PastReceiptsModal
      opened={settingsModalKey === 'pastReceipts'}
      onClose={() => setSettingsModalKey(null)}
      tenantId={tenantId}
      employeeSession={employeeSession ?? ''}
      onSelectOrder={(orderId, isCancelled) => {
        setSettingsModalKey(null);
        if (isCancelled) {
          void openRestoreBillForOrderId(orderId);
        } else {
          setPastReceiptOrderId(orderId);
        }
      }}
    />

    {pastReceiptOrderId !== null && (
      <TransactionReceiptModal
        opened
        onClose={() => setPastReceiptOrderId(null)}
        tenantId={tenantId}
        orderId={pastReceiptOrderId}
        variant="pastReceipt"
        onHeadlessPrint={(receipt, printerId, selectedJobIds) => queueHeadlessReceiptPrint(
          pastReceiptOrderId,
          receipt,
          printerId,
          selectedJobIds,
        )}
      />
    )}

    <Modal
      opened={settingsModalKey === 'printers'}
      onClose={() => setSettingsModalKey(null)}
      title="ניהול הדפסות"
      closeButtonProps={{ 'data-testid': 'settings-printers-close' } as Record<string, string>}
      size="xl"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
    >
      <PosPrintersSettingsContent
        activePrinters={activePrinters}
        selectedTestPrinterId={selectedTestPrinterId}
        isLoadingPrinters={isLoadingPrinters}
        isSendingPrinterTest={isSendingPrinterTest}
        isLoadingPrintJobs={isLoadingPrintJobs}
        isUpdatingPrintJob={isUpdatingPrintJob}
        printJobs={printJobs}
        onSelectTestPrinterId={setSelectedTestPrinterId}
        onRefreshPrinters={handleRefreshPrintersClick}
        onRefreshPrintJobs={handleRefreshPrintJobsClick}
        onCreatePrinterTestPrint={handleCreatePrinterTestPrintClick}
        onUpdatePrintJobStatus={handleUpdatePrintJobStatusClick}
      />
    </Modal>

    <Modal
      opened={settingsModalKey === 'agents'}
      onClose={() => setSettingsModalKey(null)}
      title="חיבור מדפסת"
      closeButtonProps={{ 'data-testid': 'settings-agents-close' } as Record<string, string>}
      size="xl"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
    >
      {tenantId && employeeSession && (
        <>
          <PrinterManagementSection tenantId={tenantId} />
          <PrinterRoutingSection
            tenantId={tenantId}
            employeeSession={employeeSession}
            printers={activePrinters}
          />
          <PrinterAgentsSection tenantId={tenantId} employeeSession={employeeSession} />
        </>
      )}
    </Modal>

    <Modal
      opened={settingsModalKey === 'employees'}
      onClose={() => setSettingsModalKey(null)}
      title="ניהול עובדים"
      closeButtonProps={{ 'data-testid': 'settings-employees-close' } as Record<string, string>}
      size="xl"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
    >
      <PosEmployeesSettingsContent
        form={form}
        photoFile={photoFile}
        editingEmployeeId={editingEmployeeId}
        settingsEmployees={settingsEmployees}
        isLoadingSettings={isLoadingSettings}
        isSavingEmployee={isSavingEmployee}
        attendanceUiEnabled={attendanceUiEnabled}
        onChangeForm={onChangeForm}
        onPhotoFileChange={handlePhotoFileChange}
        onRefreshEmployees={handleRefreshEmployeesClick}
        onResetForm={resetForm}
        onSaveEmployee={handleSaveEmployee}
        onEditEmployee={startEditEmployee}
        onDeactivateEmployee={handleDeactivateEmployeeClick}
        onActivateEmployee={handleActivateEmployeeClick}
      />
    </Modal>

    <AttendanceSettingsModal
      opened={settingsModalKey === 'attendance'}
      onClose={() => setSettingsModalKey(null)}
      tenantId={tenantId}
      employeeSession={employeeSession}
      employees={settingsEmployees}
      attendanceReportRules={attendanceReportRules}
    />

    {currentEmployee && (
      <AttendanceSettingsModal
        opened={settingsModalKey === 'employeeAttendanceReport'}
        onClose={() => setSettingsModalKey(null)}
        tenantId={tenantId}
        employeeSession={employeeSession}
        employees={[currentEmployee]}
        lockedEmployeeId={currentEmployee.id}
        selfServiceMode
        attendanceReportRules={attendanceReportRules}
      />
    )}

    <AttendanceRulesModal
      opened={settingsModalKey === 'attendanceRules'}
      onClose={() => setSettingsModalKey(null)}
      isSaving={isSavingAttendanceRules}
      rules={attendanceReportRules}
      onSave={(next) => {
        void handleAttendanceReportRulesSave(next);
      }}
    />

    <VariantComboSelectionModal
      opened={selectionModal !== null}
      productName={selectionModal?.product.name ?? ''}
      basePrice={selectionModal?.product.price ?? 0}
      groups={selectionModal?.groups ?? []}
      isAdding={isAddingOrderline || isAdjustingOrderline}
      initialSelections={selectionModal?.initialSelections ?? null}
      confirmLabel={selectionModal?.editingLine ? 'עדכן' : undefined}
      happyHourDiscountPct={
        happyHourEffective
          && selectionModal?.product.categoryId != null
          && happyHour?.categories.some((category) => category.id === selectionModal.product.categoryId)
          ? happyHour?.discountPct ?? null
          : null
      }
      onConfirm={handleConfirmSelections}
      onClose={closeSelectionModal}
    />

  </section>
);
};
